import type { FunctionSignature } from 'mini-code'

type ExtendedFunctionSignature = FunctionSignature & {
  examples?: string[]
}

export const functionDefinitions: Record<string, ExtendedFunctionSignature> = {
  sin: {
    name: 'sin',
    parameters: [
      { name: 'hz', type: 'number', description: 'The frequency in Hz', defaultValue: 440 },
      { name: 'trig', type: 'number', description: 'Trigger phase reset', defaultValue: 0 },
      { name: 'offset', type: 'number', description: 'Phase offset in milliseconds (reset to this on trigger)',
        defaultValue: 0 },
    ],
    returnType: 'number',
    description:
      'Returns the sine of the given frequency. Use with `trig` parameter to reset phase on beats.\n\n```\nsin(hz:440) |> out(L:%,R:%)\n```',
    examples: [
      'sin(hz:440) |> out(L:%,R:%)',
      'sin(hz:440*transpose(7)) |> out(L:%,R:%)',
      'sin(hz:440,trig:beat(1/4)) |> out(L:%,R:%)',
    ],
  },
  tri: {
    name: 'tri',
    parameters: [
      { name: 'hz', type: 'number', description: 'The frequency in Hz', defaultValue: 440 },
      { name: 'trig', type: 'number', description: 'Trigger phase reset', defaultValue: 0 },
      { name: 'offset', type: 'number', description: 'Phase offset in milliseconds (reset to this on trigger)',
        defaultValue: 0 },
    ],
    returnType: 'number',
    description: 'Returns the triangle wave of the given frequency',
    examples: [
      'tri(hz:440) |> out(L:%,R:%)',
      'tri(hz:"c2"*1) |> out(L:%,R:%)',
      'beat(1/4) |> (trig => tri(hz:440,trig:trig) |> out(L:%,R:%))',
    ],
  },
  saw: {
    name: 'saw',
    parameters: [
      { name: 'hz', type: 'number', description: 'The frequency in Hz', defaultValue: 440 },
      { name: 'trig', type: 'number', description: 'Trigger phase reset', defaultValue: 0 },
      { name: 'offset', type: 'number', description: 'Phase offset in milliseconds (reset to this on trigger)',
        defaultValue: 0 },
    ],
    returnType: 'number',
    description: 'Returns the sawtooth wave of the given frequency',
    examples: [
      'saw(hz:440) |> out(L:%,R:%)',
      'saw(hz:"c2"*1) |> out(L:%,R:%)',
      'beat(1/8) |> (trig => saw(hz:880,trig:trig) |> out(L:%,R:%))',
    ],
  },
  ramp: {
    name: 'ramp',
    parameters: [
      { name: 'hz', type: 'number', description: 'The frequency in Hz', defaultValue: 440 },
      { name: 'trig', type: 'number', description: 'Trigger phase reset', defaultValue: 0 },
      { name: 'offset', type: 'number', description: 'Phase offset in milliseconds (reset to this on trigger)',
        defaultValue: 0 },
    ],
    returnType: 'number',
    description: 'Returns the ramp wave of the given frequency',
    examples: [
      'ramp(hz:440) |> out(L:%,R:%)',
      'ramp(hz:440,trig:beat(1/4)) |> out(L:%,R:%)',
    ],
  },
  phasor: {
    name: 'phasor',
    parameters: [
      { name: 'hz', type: 'number', description: 'The frequency in Hz', defaultValue: 440 },
      { name: 'trig', type: 'number', description: 'Trigger phase reset', defaultValue: 0 },
      { name: 'offset', type: 'number', description: 'Phase offset in seconds (reset to this on trigger)',
        defaultValue: 0 },
    ],
    returnType: 'number',
    description: 'Returns the phasor (phase accumulator, outputs 0 to 1)',
    examples: [
      'phasor(hz:1/4*co,beat(4096)) |> (p => sin(hz:440*p*1000) |> out(L:%,R:%))',
      'phasor(hz:1/32*co,beat(4096)) |> (p => max(p - 0.5, 0) |> out(L:%,R:%))',
    ],
  },
  pwm: {
    name: 'pwm',
    parameters: [
      { name: 'hz', type: 'number', description: 'The frequency in Hz', defaultValue: 440 },
      {
        name: 'width',
        type: 'number',
        description: 'Pulse width (-1 to 1, phase offset)',
        defaultValue: 0,
      },
      { name: 'trig', type: 'number', description: 'Trigger phase reset', defaultValue: 0 },
    ],
    returnType: 'number',
    description: 'Returns the pulse width modulated wave of the given frequency',
    examples: [
      'pwm(hz:440,width:0.5) |> out(L:%,R:%)',
      'pwm(hz:440,width:sin(1)*0.3,trig:beat(1/4)) |> out(L:%,R:%)',
      'pwm(hz:440,width:0.6,trig:beat(1/8)) |> out(L:%,R:%)',
    ],
  },
  sqr: {
    name: 'sqr',
    parameters: [
      { name: 'hz', type: 'number', description: 'The frequency in Hz', defaultValue: 440 },
      { name: 'trig', type: 'number', description: 'Trigger phase reset', defaultValue: 0 },
      { name: 'offset', type: 'number', description: 'Phase offset in milliseconds (reset to this on trigger)',
        defaultValue: 0 },
    ],
    returnType: 'number',
    description: 'Returns the square wave of the given frequency',
    examples: [
      'sqr(hz:440) |> out(L:%,R:%)',
      'sqr(hz:"c2"*1,trig:beat(1/4)) |> out(L:%,R:%)',
      'beat(1/8) |> (trig => sqr(hz:880,trig:trig) |> out(L:%,R:%))',
    ],
  },
  lfosin: {
    name: 'lfosin',
    parameters: [
      { name: 'beat', type: 'number',
        description: 'Beat interval (e.g., 1/4, 1/8, 1/16). Defaults to 1/4 (quarter note).', defaultValue: 0.25 },
      { name: 'offset', type: 'number', description: 'Phase offset in beats (e.g., -1/16 for negative offset)',
        defaultValue: 0 },
      { name: 'trig', type: 'number', description: 'Trigger phase reset', defaultValue: 0 },
    ],
    returnType: 'number',
    description: 'Returns a beat-synced sine LFO (control rate, evaluates once per block)',
    examples: [
      'sin(hz:440+lfosin(beat:1/4)*200) |> out(L:%,R:%)',
      'lp(in:sin(hz:440),cut:500+lfosin(beat:1/2)*500) |> out(L:%,R:%)',
    ],
  },
  lfotri: {
    name: 'lfotri',
    parameters: [
      { name: 'beat', type: 'number',
        description: 'Beat interval (e.g., 1/4, 1/8, 1/16). Defaults to 1/4 (quarter note).', defaultValue: 0.25 },
      { name: 'offset', type: 'number', description: 'Phase offset in beats (e.g., -1/16 for negative offset)',
        defaultValue: 0 },
      { name: 'trig', type: 'number', description: 'Trigger phase reset', defaultValue: 0 },
    ],
    returnType: 'number',
    description: 'Returns a beat-synced triangle LFO (control rate, evaluates once per block)',
    examples: [
      'sin(hz:440+lfotri(beat:1/4)*200) |> out(L:%,R:%)',
      'lp(in:sin(hz:440),cut:1000+lfotri(beat:1/8)*500) |> out(L:%,R:%)',
    ],
  },
  lfosaw: {
    name: 'lfosaw',
    parameters: [
      { name: 'beat', type: 'number',
        description: 'Beat interval (e.g., 1/4, 1/8, 1/16). Defaults to 1/4 (quarter note).', defaultValue: 0.25 },
      { name: 'offset', type: 'number', description: 'Phase offset in beats (e.g., -1/16 for negative offset)',
        defaultValue: 0 },
      { name: 'trig', type: 'number', description: 'Trigger phase reset', defaultValue: 0 },
    ],
    returnType: 'number',
    description: 'Returns a beat-synced sawtooth LFO (control rate, evaluates once per block)',
    examples: [
      'sin(hz:440+lfosaw(beat:1/4)*200) |> out(L:%,R:%)',
      'lp(in:sin(hz:440),cut:500+lfosaw(beat:1/2)*1000) |> out(L:%,R:%)',
    ],
  },
  lforamp: {
    name: 'lforamp',
    parameters: [
      { name: 'beat', type: 'number',
        description: 'Beat interval (e.g., 1/4, 1/8, 1/16). Defaults to 1/4 (quarter note).', defaultValue: 0.25 },
      { name: 'offset', type: 'number', description: 'Phase offset in beats (e.g., -1/16 for negative offset)',
        defaultValue: 0 },
      { name: 'trig', type: 'number', description: 'Trigger phase reset', defaultValue: 0 },
    ],
    returnType: 'number',
    description: 'Returns a beat-synced ramp LFO (control rate, evaluates once per block)',
    examples: [
      'sin(hz:440+lforamp(beat:1/4)*200) |> out(L:%,R:%)',
      'lp(in:sin(hz:440),cut:500+lforamp(beat:1/8)*500) |> out(L:%,R:%)',
    ],
  },
  lfosqr: {
    name: 'lfosqr',
    parameters: [
      { name: 'beat', type: 'number',
        description: 'Beat interval (e.g., 1/4, 1/8, 1/16). Defaults to 1/4 (quarter note).', defaultValue: 0.25 },
      { name: 'offset', type: 'number', description: 'Phase offset in beats (e.g., -1/16 for negative offset)',
        defaultValue: 0 },
      { name: 'trig', type: 'number', description: 'Trigger phase reset', defaultValue: 0 },
    ],
    returnType: 'number',
    description: 'Returns a beat-synced square LFO (control rate, evaluates once per block)',
    examples: [
      'sin(hz:440+lfosqr(beat:1/4)*200) |> out(L:%,R:%)',
      'lp(in:sin(hz:440),cut:500+lfosqr(beat:1/2)*500) |> out(L:%,R:%)',
    ],
  },
  lfopwm: {
    name: 'lfopwm',
    parameters: [
      { name: 'beat', type: 'number',
        description: 'Beat interval (e.g., 1/4, 1/8, 1/16). Defaults to 1/4 (quarter note).', defaultValue: 0.25 },
      { name: 'offset', type: 'number', description: 'Phase offset in beats (e.g., -1/16 for negative offset)',
        defaultValue: 0 },
      { name: 'width', type: 'number', description: 'Pulse width (-1 to 1, phase offset)', defaultValue: 0 },
      { name: 'trig', type: 'number', description: 'Trigger phase reset', defaultValue: 0 },
    ],
    returnType: 'number',
    description: 'Returns a beat-synced pulse width modulated LFO (control rate, evaluates once per block)',
    examples: [
      'sin(hz:440+lfopwm(beat:1/4,width:0.5)*200) |> out(L:%,R:%)',
      'lp(in:sin(hz:440),cut:500+lfopwm(beat:1/8,width:0.3)*500) |> out(L:%,R:%)',
    ],
  },
  lfosah: {
    name: 'lfosah',
    parameters: [
      { name: 'beat', type: 'number',
        description: 'Beat interval (e.g., 1/4, 1/8, 1/16). Defaults to 1/4 (quarter note).', defaultValue: 0.25 },
      {
        name: 'seed',
        type: 'number',
        description: 'The seed for the random generator',
        defaultValue: 0,
      },
      { name: 'offset', type: 'number', description: 'Phase offset in beats (e.g., -1/16 for negative offset)',
        defaultValue: 0 },
      { name: 'trig', type: 'number', description: 'Trigger phase reset', defaultValue: 0 },
    ],
    returnType: 'number',
    description:
      'Returns a beat-synced sample-and-hold random LFO (control rate, evaluates once per block). Generates a new random value (-1 to 1) at each beat cycle and holds it until the next cycle.',
    examples: [
      'sin(hz:440+lfosah(beat:1/4)*200) |> out(L:%,R:%)',
      'lp(in:sin(hz:440),cut:500+lfosah(beat:1/8)*1000) |> out(L:%,R:%)',
    ],
  },
  harmonics: {
    name: 'harmonics',
    parameters: [
      { name: 'hz', type: 'number', description: 'The base frequency in Hz', defaultValue: 440 },
      {
        name: 'partials',
        type: 'array',
        description: 'Array of amplitudes for each harmonic (e.g., [1, 0.5, 0.2])',
      },
      { name: 'trig', type: 'number', description: 'Trigger phase reset', defaultValue: 0 },
    ],
    returnType: 'number',
    description: 'Additive synthesis with multiple harmonics at integer multiples of the base frequency',
    examples: [
      'harmonics(hz:440,partials:[1,0.5,0.2],trig:beat(1/4)) |> out(L:%,R:%)',
      'harmonics(hz:220,partials:[1,0.7,0.5,0.3,0.1]) |> out(L:%,R:%)',
    ],
  },
  noise: {
    name: 'noise',
    parameters: [
      {
        name: 'seed',
        type: 'number',
        description: 'The seed for the noise generator',
        defaultValue: 0,
      },
      {
        name: 'type',
        type: 'string',
        description: 'The type of noise: "white", "pink", or "brown"',
        defaultValue: 'white',
      },
      { name: 'trig', type: 'number', description: 'Trigger phase reset', defaultValue: 0 },
    ],
    returnType: 'number',
    description: 'Returns the noise of the given seed',
    examples: [
      'noise(seed:1234) |> out(L:%,R:%)',
      'noise(seed:342,type:"brown") |> out(L:%,R:%)',
      'beat(1/16) |> (trig => noise(seed:1234,trig:trig)*ar(attack:0.01,release:0.1,trig:trig) |> out(L:%,R:%))',
    ],
  },
  grain: {
    name: 'grain',
    parameters: [
      { name: 'speed', type: 'number', description: 'The speed of grain impulses (0..1)' },
    ],
    returnType: 'number',
    description: 'Returns impulses at random intervals based on speed',
    examples: [
      '"c4 c5 c6".walk(1/16,grain(0.5)) |> (hz => sin(hz:hz,trig:grain(0.5)) |> out(L:%,R:%))',
      'grain(0.3) |> sin(hz:440,trig:%) |> out(L:%,R:%)',
    ],
  },
  beatgrain: {
    name: 'beatgrain',
    parameters: [
      { name: 'beat', type: 'number', description: 'The beat interval (e.g. 1/4, 1/16)' },
      {
        name: 'prob',
        type: 'number',
        defaultValue: 1,
        description: 'The probability of impulse (0..1, 1=always, 0=never)',
      },
    ],
    returnType: 'number',
    description: 'Returns beat-synced impulses with probability control',
    deprecated: true,
  },
  beat: {
    name: 'beat',
    parameters: [
      { name: 'beat', type: 'number', description: 'The beat interval (e.g. 1/4, 1/16)' },
      {
        name: 'prob',
        type: 'number',
        defaultValue: 1,
        description: 'The probability of impulse (0..1, 1=always, 0=never)',
      },
      {
        name: 'swing',
        type: 'number',
        defaultValue: 0,
        description: 'Swing amount (0-1, default 0)',
      },
      {
        name: 'offset',
        type: 'number',
        defaultValue: 0,
        description: 'Offset in seconds to shift the impulses',
      },
      {
        name: 'skipFirst',
        type: 'number',
        defaultValue: 0,
        description: 'Skip the first beat on initialization (1 to skip, 0 to trigger)',
      },
    ],
    returnType: 'number',
    description: 'Returns beat-synced impulses with probability control',
    examples: [
      'beat(1/4) |> sin(hz:440,trig:%) |> out(L:%,R:%)',
      'beat(1/16,prob:0.7) |> sin(hz:880,trig:%) |> out(L:%,R:%)',
      'beat(1/8,swing:0.1) |> sin(hz:440,trig:%) |> out(L:%,R:%)',
    ],
  },
  skip: {
    name: 'skip',
    parameters: [
      { name: 'in', type: 'number', description: 'Input signal to process' },
      {
        name: 'count',
        type: 'number',
        defaultValue: 1,
        description: 'Skip every Nth trigger (e.g. count:3 skips every 3rd trigger)',
      },
      {
        name: 'offset',
        type: 'number',
        defaultValue: 0,
        description: 'Starting count offset - counter starts at this value on init and when reset by trig',
      },
      {
        name: 'trig',
        type: 'number',
        defaultValue: 0,
        description: 'Reset trigger - resets the counter to offset on rising edge',
      },
    ],
    returnType: 'number',
    description:
      'Passes through input signal as-is, but skips (outputs 0) every count-th trigger. The trig parameter resets the counter to offset. The offset parameter sets the starting count. Example: skip(beat(1/16), 3, 1) will start counting from 1, pass through triggers 1 and 2, skip trigger 3, etc.',
    examples: [
      'beat(1/16) |> skip(%,count:3) |> sin(hz:440,trig:%) |> out(L:%,R:%)',
      'skip(beat(1/16),count:4,offset:1) |> sin(hz:880,trig:%) |> out(L:%,R:%)',
    ],
  },
  every: {
    name: 'every',
    parameters: [
      { name: 'in', type: 'number', description: 'Input signal to process' },
      {
        name: 'count',
        type: 'number',
        defaultValue: 1,
        description: 'Allow only every Nth trigger to pass (e.g. count:3 allows only every 3rd trigger)',
      },
      {
        name: 'offset',
        type: 'number',
        defaultValue: 0,
        description: 'Starting count offset - counter starts at this value on init and when reset by trig',
      },
      {
        name: 'trig',
        type: 'number',
        defaultValue: 0,
        description: 'Reset trigger - resets the counter to offset on rising edge',
      },
    ],
    returnType: 'number',
    description:
      'Allows only every count-th trigger to pass through (outputs 0 for all others), opposite of skip. The trig parameter resets the counter to offset. The offset parameter sets the starting count. Example: every(beat(1/16), 3) will block triggers 1 and 2, pass through trigger 3, block 4 and 5, pass through 6, etc. Can be used with pipe: beat(1/16) |> every(%, 3)',
    examples: [
      'beat(1/16) |> every(%,count:3) |> sin(hz:440,trig:%) |> out(L:%,R:%)',
      'every(beat(1/8),count:4) |> sin(hz:880,trig:%) |> out(L:%,R:%)',
    ],
  },
  euclid: {
    name: 'euclid',
    parameters: [
      {
        name: 'beat',
        type: 'number',
        defaultValue: 0.25,
        description: 'Beat interval for each step (e.g., 1/4, 1/8, 1/16). Defaults to 1/4 (quarter note).',
      },
      { name: 'hits', type: 'number', description: 'Number of hits in the pattern' },
      { name: 'steps', type: 'number', description: 'Number of steps in the pattern' },
    ],
    returnType: 'number',
    description:
      'Returns euclidean rhythm impulses synced to global clock beat. Distributes hits evenly across steps at the specified beat interval. Pattern repeats every steps beats.',
    examples: [
      'euclid(beat:1/16,hits:3,steps:8) |> sin(hz:440,trig:%) |> out(L:%,R:%)',
      'euclid(beat:1/8,hits:5,steps:12) |> sin(hz:880,trig:%) |> out(L:%,R:%)',
    ],
  },
  subdivide: {
    name: 'subdivide',
    parameters: [
      {
        name: 'beat',
        type: 'number',
        defaultValue: 0.25,
        description:
          'Beat interval for the total span of the subdivision cycle (e.g., 1/4, 1/8, 1/16). Defaults to 1/4 (quarter note).',
      },
      {
        name: 'pattern',
        type: 'array',
        description: 'Proportional breakdown array (e.g., [1,2,3] divides into 3 parts with ratios 1:2:3)',
      },
    ],
    returnType: 'number',
    description:
      'Returns impulse signals (>0 for one frame) at each subdivision boundary. The pattern array defines proportional breakdown of the beat cycle. Example: subdivide(beat:1/4, pattern:[1,2,3]) divides a quarter note into three parts with ratios 1:2:3.',
    examples: [
      '"c4 c5 c6".subdivide(beat:1/4,pattern:[1,2,3],cb:(trig,hz) => sin(hz:hz,trig:trig) |> out(L:%,R:%))',
      'subdivide(beat:1/8,pattern:[1,1,2]) |> sin(hz:440,trig:%) |> out(L:%,R:%)',
    ],
  },
  ar: {
    name: 'ar',
    parameters: [
      {
        name: 'attack',
        type: 'number',
        description: 'The attack time in seconds',
        defaultValue: 0.1,
      },
      {
        name: 'release',
        type: 'number',
        description: 'The release time in seconds',
        defaultValue: 0.5,
      },
      {
        name: 'exponent',
        type: 'number',
        description: 'The exponent for the envelope',
        defaultValue: 1,
      },
      { name: 'trig', type: 'number', description: 'Trigger phase reset', defaultValue: 0 },
    ],
    returnType: 'number',
    description: 'Returns the attack-release envelope of the given parameters',
    examples: [
      'beat(1/4) |> (trig => sin(hz:440,trig:trig)*ar(attack:0.01,release:0.5,trig:trig) |> out(L:%,R:%))',
      'sin(hz:440)*ar(attack:0.1,release:1.0,exponent:2,trig:beat(1/4)) |> out(L:%,R:%)',
    ],
  },
  adsr: {
    name: 'adsr',
    parameters: [
      {
        name: 'attack',
        type: 'number',
        description: 'The attack time in seconds',
        defaultValue: 0.1,
      },
      {
        name: 'decay',
        type: 'number',
        description: 'The decay time in seconds',
        defaultValue: 0.1,
      },
      {
        name: 'sustain',
        type: 'number',
        description: 'The sustain level (0..1)',
        defaultValue: 0.7,
      },
      {
        name: 'release',
        type: 'number',
        description: 'The release time in seconds',
        defaultValue: 0.5,
      },
      {
        name: 'exponent',
        type: 'number',
        description: 'The exponent for the envelope',
        defaultValue: 1,
      },
      {
        name: 'trig',
        type: 'number',
        description: 'Trigger signal (hold while >0)',
        defaultValue: 0,
      },
    ],
    returnType: 'number',
    description: 'Returns the attack-decay-sustain-release envelope of the given parameters',
    examples: [
      'beat(1/4) |> (trig => sin(hz:440)*adsr(attack:0.01,decay:0.1,sustain:0.7,release:0.3,trig:trig) |> out(L:%,R:%))',
      'beat(1/8) |> (trig => sin(hz:880)*adsr(attack:0.05,decay:0.2,sustain:0.5,release:0.5,trig:trig) |> out(L:%,R:%))',
    ],
  },
  lp: {
    name: 'lp',
    parameters: [
      { name: 'in', type: 'number', description: 'The input signal', defaultValue: 0 },
      {
        name: 'cut',
        type: 'number',
        description: 'The cutoff frequency in Hz',
        defaultValue: 1000,
      },
      { name: 'q', type: 'number', description: 'The Q factor', defaultValue: 0.707 },
    ],
    returnType: 'number',
    description: 'Returns the low-pass filtered signal of the given parameters',
    examples: [
      'sin(hz:440) |> lp(in:%,cut:500,q:1) |> out(L:%,R:%)',
      'sin(hz:880) |> lp(in:%,cut:lfosin(beat:1/2)*500+500) |> out(L:%,R:%)',
    ],
  },
  hp: {
    name: 'hp',
    parameters: [
      { name: 'in', type: 'number', description: 'The input signal', defaultValue: 0 },
      {
        name: 'cut',
        type: 'number',
        description: 'The cutoff frequency in Hz',
        defaultValue: 1000,
      },
      { name: 'q', type: 'number', description: 'The Q factor', defaultValue: 0.707 },
    ],
    returnType: 'number',
    description: 'Returns the high-pass filtered signal of the given parameters',
    examples: [
      'noise(seed:1234) |> hp(in:%,cut:5000,q:1) |> out(L:%,R:%)',
      'sin(hz:440) |> hp(in:%,cut:200) |> out(L:%,R:%)',
    ],
  },
  bp: {
    name: 'bp',
    parameters: [
      { name: 'in', type: 'number', description: 'The input signal', defaultValue: 0 },
      {
        name: 'cut',
        type: 'number',
        description: 'The cutoff frequency in Hz',
        defaultValue: 1000,
      },
      { name: 'q', type: 'number', description: 'The Q factor', defaultValue: 0.707 },
    ],
    returnType: 'number',
    description: 'Returns the band-pass filtered signal of the given parameters',
    examples: [
      'noise(seed:1234) |> bp(in:%,cut:8000,q:2) |> out(L:%,R:%)',
      'sin(hz:440) |> bp(in:%,cut:1000,q:1.5) |> out(L:%,R:%)',
    ],
  },
  svflp: {
    name: 'svflp',
    parameters: [
      { name: 'in', type: 'number', description: 'The input signal', defaultValue: 0 },
      {
        name: 'cut',
        type: 'number',
        description: 'The cutoff frequency in Hz',
        defaultValue: 1000,
      },
      { name: 'q', type: 'number', description: 'The Q factor', defaultValue: 0.5 },
    ],
    returnType: 'number',
    description: 'Returns the SVF low-pass filtered signal of the given parameters',
    examples: [
      'sin(hz:440) |> svflp(in:%,cut:500,q:0.5) |> out(L:%,R:%)',
      'saw(hz:440) |> svflp(in:%,cut:1500+sin(1/4)*500,q:0.7) |> out(L:%,R:%)',
    ],
  },
  svfhp: {
    name: 'svfhp',
    parameters: [
      { name: 'in', type: 'number', description: 'The input signal', defaultValue: 0 },
      {
        name: 'cut',
        type: 'number',
        description: 'The cutoff frequency in Hz',
        defaultValue: 1000,
      },
      { name: 'q', type: 'number', description: 'The Q factor', defaultValue: 0.5 },
    ],
    returnType: 'number',
    description: 'Returns the SVF high-pass filtered signal of the given parameters',
    examples: [
      'noise(seed:1234) |> svfhp(in:%,cut:5000,q:0.5) |> out(L:%,R:%)',
      'sin(hz:440) |> svfhp(in:%,cut:200) |> out(L:%,R:%)',
    ],
  },
  svfbp: {
    name: 'svfbp',
    parameters: [
      { name: 'in', type: 'number', description: 'The input signal', defaultValue: 0 },
      {
        name: 'cut',
        type: 'number',
        description: 'The cutoff frequency in Hz',
        defaultValue: 1000,
      },
      { name: 'q', type: 'number', description: 'The Q factor', defaultValue: 0.5 },
    ],
    returnType: 'number',
    description: 'Returns the SVF band-pass filtered signal of the given parameters',
    examples: [
      'noise(seed:1234) |> svfbp(in:%,cut:8000,q:0.5) |> out(L:%,R:%)',
      'sin(hz:440) |> svfbp(in:%,cut:1000,q:0.7) |> out(L:%,R:%)',
    ],
  },
  svfnotch: {
    name: 'svfnotch',
    parameters: [
      { name: 'in', type: 'number', description: 'The input signal', defaultValue: 0 },
      {
        name: 'cut',
        type: 'number',
        description: 'The cutoff frequency in Hz',
        defaultValue: 1000,
      },
      { name: 'q', type: 'number', description: 'The Q factor', defaultValue: 0.5 },
    ],
    returnType: 'number',
    description: 'Returns the SVF notch filtered signal of the given parameters',
    examples: [
      'sin(hz:440) |> svfnotch(in:%,cut:440,q:0.5) |> out(L:%,R:%)',
      'saw(hz:440) |> svfnotch(in:%,cut:880,q:1) |> out(L:%,R:%)',
    ],
  },
  svfpeak: {
    name: 'svfpeak',
    parameters: [
      { name: 'in', type: 'number', description: 'The input signal', defaultValue: 0 },
      {
        name: 'cut',
        type: 'number',
        description: 'The cutoff frequency in Hz',
        defaultValue: 1000,
      },
      { name: 'q', type: 'number', description: 'The Q factor', defaultValue: 0.5 },
    ],
    returnType: 'number',
    description: 'Returns the SVF peak filtered signal of the given parameters',
    examples: [
      'sin(hz:440) |> svfpeak(in:%,cut:1000,q:0.5) |> out(L:%,R:%)',
      'saw(hz:440) |> svfpeak(in:%,cut:2000,q:1) |> out(L:%,R:%)',
    ],
  },
  svfallpass: {
    name: 'svfallpass',
    parameters: [
      { name: 'in', type: 'number', description: 'The input signal', defaultValue: 0 },
      {
        name: 'cut',
        type: 'number',
        description: 'The cutoff frequency in Hz',
        defaultValue: 1000,
      },
      { name: 'q', type: 'number', description: 'The Q factor', defaultValue: 0.5 },
    ],
    returnType: 'number',
    description: 'Returns the SVF all-pass filtered signal of the given parameters',
    examples: [
      'sin(hz:440) |> svfallpass(in:%,cut:1000,q:0.5) |> out(L:%,R:%)',
      'saw(hz:440) |> svfallpass(in:%,cut:500,q:0.7) |> out(L:%,R:%)',
    ],
  },
  bs: {
    name: 'bs',
    parameters: [
      { name: 'in', type: 'number', description: 'The input signal', defaultValue: 0 },
      {
        name: 'cut',
        type: 'number',
        description: 'The cutoff frequency in Hz',
        defaultValue: 1000,
      },
      { name: 'q', type: 'number', description: 'The Q factor', defaultValue: 0.707 },
    ],
    returnType: 'number',
    description: 'Returns the band-stop (notch) filtered signal of the given parameters',
    examples: [
      'sin(hz:440) |> bs(in:%,cut:440,q:0.707) |> out(L:%,R:%)',
      'saw(hz:440) |> bs(in:%,cut:880,q:1) |> out(L:%,R:%)',
    ],
  },
  notch: {
    name: 'notch',
    parameters: [
      { name: 'in', type: 'number', description: 'The input signal', defaultValue: 0 },
      {
        name: 'cut',
        type: 'number',
        description: 'The cutoff frequency in Hz',
        defaultValue: 1000,
      },
      { name: 'q', type: 'number', description: 'The Q factor', defaultValue: 0.707 },
    ],
    returnType: 'number',
    description: 'Returns the notch filtered signal of the given parameters',
    examples: [
      'sin(hz:440) |> notch(in:%,cut:440,q:0.707) |> out(L:%,R:%)',
      'saw(hz:440) |> notch(in:%,cut:880,q:1) |> out(L:%,R:%)',
    ],
  },
  ls: {
    name: 'ls',
    parameters: [
      { name: 'in', type: 'number', description: 'The input signal', defaultValue: 0 },
      {
        name: 'cut',
        type: 'number',
        description: 'The cutoff frequency in Hz',
        defaultValue: 1000,
      },
      { name: 'gain', type: 'number', description: 'The gain in dB', defaultValue: 0 },
    ],
    returnType: 'number',
    description: 'Returns the low-shelf filtered signal of the given parameters',
    examples: [
      'sin(hz:440) |> ls(in:%,cut:500,gain:3) |> out(L:%,R:%)',
      'saw(hz:440) |> ls(in:%,cut:200,gain:-6) |> out(L:%,R:%)',
    ],
  },
  hs: {
    name: 'hs',
    parameters: [
      { name: 'in', type: 'number', description: 'The input signal', defaultValue: 0 },
      {
        name: 'cut',
        type: 'number',
        description: 'The cutoff frequency in Hz',
        defaultValue: 1000,
      },
      { name: 'gain', type: 'number', description: 'The gain in dB', defaultValue: 0 },
    ],
    returnType: 'number',
    description: 'Returns the high-shelf filtered signal of the given parameters',
    examples: [
      'sin(hz:440) |> hs(in:%,cut:5000,gain:3) |> out(L:%,R:%)',
      'saw(hz:440) |> hs(in:%,cut:8000,gain:-6) |> out(L:%,R:%)',
    ],
  },
  peak: {
    name: 'peak',
    parameters: [
      { name: 'in', type: 'number', description: 'The input signal', defaultValue: 0 },
      {
        name: 'cut',
        type: 'number',
        description: 'The cutoff frequency in Hz',
        defaultValue: 1000,
      },
      { name: 'q', type: 'number', description: 'The Q factor', defaultValue: 0.707 },
      { name: 'gain', type: 'number', description: 'The gain in dB', defaultValue: 0 },
    ],
    returnType: 'number',
    description: 'Returns the peak filtered signal of the given parameters',
    examples: [
      'sin(hz:440) |> peak(in:%,cut:2000,q:1,gain:6) |> out(L:%,R:%)',
      'saw(hz:440) |> peak(in:%,cut:1000,q:0.707,gain:-12) |> out(L:%,R:%)',
    ],
  },
  eq3: {
    name: 'eq3',
    parameters: [
      { name: 'in', type: 'number', description: 'The input signal', defaultValue: 0 },
      { name: 'low', type: 'number', description: 'The low shelf gain in dB', defaultValue: 0 },
      { name: 'mid', type: 'number', description: 'The mid peak gain in dB', defaultValue: 0 },
      { name: 'high', type: 'number', description: 'The high shelf gain in dB', defaultValue: 0 },
    ],
    returnType: 'number',
    description: 'Returns the 3-band EQ filtered signal (low shelf at 500Hz, mid peak at 2kHz, high shelf at 8kHz)',
    examples: [
      'sin(hz:440) |> eq3(in:%,low:3,mid:0,high:-3) |> out(L:%,R:%)',
      'saw(hz:440) |> eq3(in:%,low:-2,mid:4,high:2) |> out(L:%,R:%)',
    ],
  },
  allpass: {
    name: 'allpass',
    parameters: [
      { name: 'in', type: 'number', description: 'The input signal', defaultValue: 0 },
      {
        name: 'cut',
        type: 'number',
        description: 'The cutoff frequency in Hz',
        defaultValue: 1000,
      },
      { name: 'q', type: 'number', description: 'The Q factor', defaultValue: 0.707 },
    ],
    returnType: 'number',
    description: 'Returns the all-pass filtered signal of the given parameters',
    examples: [
      'sin(hz:440) |> allpass(in:%,cut:1000,q:0.707) |> out(L:%,R:%)',
      'saw(hz:440) |> allpass(in:%,cut:500,q:1) |> out(L:%,R:%)',
    ],
  },
  diodeladder: {
    name: 'diodeladder',
    parameters: [
      { name: 'in', type: 'number', description: 'The input signal', defaultValue: 0 },
      {
        name: 'cut',
        type: 'number',
        description: 'The cutoff frequency in Hz',
        defaultValue: 1000,
      },
      { name: 'q', type: 'number', description: 'The resonance (0-1)', defaultValue: 0.5 },
      {
        name: 'hpcut',
        type: 'number',
        description: 'The high-pass filter frequency in Hz',
        defaultValue: 1000,
      },
      {
        name: 'sat',
        type: 'number',
        description: 'The saturation amount (0.1-10)',
        defaultValue: 0.5,
      },
    ],
    returnType: 'number',
    description: 'Returns the diode ladder filtered signal (4-pole lowpass with resonance and saturation)',
    examples: [
      'saw(hz:440) |> diodeladder(in:%,cut:200+1000*ar(attack:0.01,release:0.2,trig:beat(1/4)),q:0.93,hpcut:30,sat:0.99) |> out(L:%,R:%)',
      'tri(hz:440) |> diodeladder(in:%,cut:400,q:0.8,hpcut:40,sat:0.9) |> out(L:%,R:%)',
    ],
  },
  mooglp: {
    name: 'mooglp',
    parameters: [
      { name: 'in', type: 'number', description: 'The input signal', defaultValue: 0 },
      {
        name: 'cut',
        type: 'number',
        description: 'The cutoff frequency in Hz (50-22040)',
        defaultValue: 1000,
      },
      { name: 'q', type: 'number', description: 'The Q factor (0.01-0.985)', defaultValue: 0.5 },
    ],
    returnType: 'number',
    description: 'Returns the Moog ladder low-pass filtered signal',
    examples: [
      'tri(hz:440) |> mooglp(in:%,cut:80,q:0.85) |> out(L:%,R:%)',
      'saw(hz:440) |> mooglp(in:%,cut:150,q:0.8) |> out(L:%,R:%)',
    ],
  },
  mooghp: {
    name: 'mooghp',
    parameters: [
      { name: 'in', type: 'number', description: 'The input signal', defaultValue: 0 },
      {
        name: 'cut',
        type: 'number',
        description: 'The cutoff frequency in Hz (50-22040)',
        defaultValue: 1000,
      },
      { name: 'q', type: 'number', description: 'The Q factor (0.01-0.985)', defaultValue: 0.5 },
    ],
    returnType: 'number',
    description: 'Returns the Moog ladder high-pass filtered signal',
    examples: [
      'sin(hz:440) |> mooghp(in:%,cut:200,q:0.5) |> out(L:%,R:%)',
      'saw(hz:440) |> mooghp(in:%,cut:100,q:0.7) |> out(L:%,R:%)',
    ],
  },
  ffthp: {
    name: 'ffthp',
    parameters: [
      { name: 'in', type: 'number', description: 'The input signal', defaultValue: 0 },
      {
        name: 'cut',
        type: 'number',
        description: 'The cutoff frequency in Hz (20-22050)',
        defaultValue: 1000,
      },
      {
        name: 'slope',
        type: 'number',
        description: 'The slope steepness (0.1-10, higher = steeper)',
        defaultValue: 2.0,
      },
    ],
    returnType: 'number',
    description: 'Returns the FFT-based high-pass filtered signal that completely eliminates frequencies below cutoff',
    examples: [
      'sin(hz:440) |> ffthp(in:%,cut:200,slope:2) |> out(L:%,R:%)',
      'saw(hz:440) |> ffthp(in:%,cut:500,slope:4) |> out(L:%,R:%)',
    ],
  },
  delay: {
    name: 'delay',
    parameters: [
      { name: 'in', type: 'number', description: 'The input signal', defaultValue: 0 },
      { name: 'time', type: 'number', description: 'The delay time in seconds', defaultValue: 0.5 },
      { name: 'feedback', type: 'number', description: 'The feedback amount', defaultValue: 0.5 },
    ],
    returnType: 'number',
    description: 'Returns the delayed signal of the given parameters',
    examples: [
      'sin(hz:440) |> delay(in:%,time:0.3,feedback:0.4) |> out(L:%,R:%)',
      'sin(hz:440) |> delay(in:%,time:0.2,feedback:0.6) |> delay(in:%,time:0.15,feedback:0.3) |> out(L:%,R:%)',
    ],
  },
  comb: {
    name: 'comb',
    parameters: [
      { name: 'in', type: 'number', description: 'The input signal', defaultValue: 0 },
      {
        name: 'delay',
        type: 'number',
        description: 'The delay time in seconds',
        defaultValue: 0.5,
      },
      { name: 'feedback', type: 'number', description: 'The feedback amount', defaultValue: 0.5 },
    ],
    returnType: 'number',
    description: 'Returns the comb filtered signal (input mixed with delayed signal)',
    examples: [
      'sin(hz:440) |> comb(in:%,delay:0.1,feedback:0.5) |> out(L:%,R:%)',
      'saw(hz:440) |> comb(in:%,delay:0.05,feedback:0.7) |> out(L:%,R:%)',
    ],
  },
  beatdelay: {
    name: 'beatdelay',
    parameters: [
      { name: 'in', type: 'number', description: 'The input signal', defaultValue: 0 },
      { name: 'beat', type: 'number', description: 'The delay time in beats', defaultValue: 1 / 4 },
      { name: 'feedback', type: 'number', description: 'The feedback amount', defaultValue: 0.5 },
    ],
    returnType: 'number',
    description: 'Returns the beat-synced delayed signal of the given parameters',
    examples: [
      'sin(hz:440) |> beatdelay(in:%,beat:1/4,feedback:0.5) |> out(L:%,R:%)',
      'saw(hz:440) |> beatdelay(in:%,beat:1/8,feedback:0.6) |> out(L:%,R:%)',
    ],
  },
  chorus: {
    name: 'chorus',
    parameters: [
      { name: 'in', type: 'number', description: 'The input signal', defaultValue: 0 },
      {
        name: 'depth',
        type: 'number',
        description: 'Modulation depth in milliseconds (0-20)',
        defaultValue: 5,
      },
      {
        name: 'lfo',
        type: 'number',
        description: 'LFO modulation signal (e.g., sin(1))',
        defaultValue: 0,
      },
    ],
    returnType: 'number',
    description: 'Returns the chorused signal (input mixed with delayed, LFO-modulated copy)',
    examples: [
      'sin(hz:440) |> chorus(in:%,depth:5,lfo:sin(1)) |> out(L:%,R:%)',
      'saw(hz:440) |> chorus(in:%,depth:10,lfo:sin(0.5)) |> out(L:%,R:%)',
    ],
  },
  flanger: {
    name: 'flanger',
    parameters: [
      { name: 'in', type: 'number', description: 'The input signal', defaultValue: 0 },
      {
        name: 'depth',
        type: 'number',
        description: 'Modulation depth in milliseconds (0-10)',
        defaultValue: 2,
      },
      {
        name: 'rate',
        type: 'number',
        description: 'LFO rate in Hz',
        defaultValue: 0.5,
      },
      {
        name: 'feedback',
        type: 'number',
        description: 'Feedback amount (0-1)',
        defaultValue: 0.3,
      },
    ],
    returnType: 'number',
    description: 'Returns the flanged signal (input mixed with delayed, LFO-modulated copy with feedback)',
    examples: [
      'sin(hz:440) |> flanger(in:%,depth:2,rate:0.5,feedback:0.3) |> out(L:%,R:%)',
      'saw(hz:440) |> flanger(in:%,depth:4,rate:1,feedback:0.5) |> out(L:%,R:%)',
    ],
  },
  dattorro: {
    name: 'dattorro',
    parameters: [
      { name: 'in', type: 'number', description: 'The input signal', defaultValue: 0 },
      {
        name: 'diffusion1',
        type: 'number',
        description: 'Input diffusion coefficient 1 (0-1)',
        defaultValue: 0.5,
      },
      {
        name: 'diffusion2',
        type: 'number',
        description: 'Input diffusion coefficient 2 (0-1)',
        defaultValue: 0.5,
      },
      {
        name: 'damping',
        type: 'number',
        description: 'High-frequency damping (0-1)',
        defaultValue: 0.5,
      },
      { name: 'decay', type: 'number', description: 'Feedback decay (0-0.99)', defaultValue: 0.5 },
    ],
    returnType: 'number',
    description: 'Dattorro-style algorithmic reverb (wet-only output)',
    examples: [
      'sin(hz:440) |> dattorro(in:%,diffusion1:0.7,diffusion2:0.5,damping:0.005,decay:0.5) |> out(L:%,R:%)',
      'saw(hz:440) |> dattorro(in:%,diffusion1:0.45,diffusion2:0.45,damping:0.96,decay:0.71) |> out(L:%,R:%)',
    ],
  },
  freeverb: {
    name: 'freeverb',
    parameters: [
      { name: 'in', type: 'number', description: 'The input signal', defaultValue: 0 },
      { name: 'roomsize', type: 'number', description: 'Room size (0-1)', defaultValue: 0.5 },
      {
        name: 'damping',
        type: 'number',
        description: 'High-frequency damping (0-1)',
        defaultValue: 0.5,
      },
    ],
    returnType: 'number',
    description: 'Classic Freeverb reverb algorithm (wet-only output)',
    examples: [
      'sin(hz:440) |> freeverb(in:%,roomsize:0.5,damping:0.5) |> out(L:%,R:%)',
      'saw(hz:440) |> freeverb(in:%,roomsize:0.7,damping:0.3) |> out(L:%,R:%)',
    ],
  },
  fdn: {
    name: 'fdn',
    parameters: [
      { name: 'in', type: 'number', description: 'The input signal', defaultValue: 0 },
      { name: 'size', type: 'number', description: 'Room size (0.1-2.0)', defaultValue: 1 },
      {
        name: 'damping',
        type: 'number',
        description: 'High-frequency damping (0-1)',
        defaultValue: 0.5,
      },
      { name: 'decay', type: 'number', description: 'Feedback decay (0-0.999)', defaultValue: 0.5 },
    ],
    returnType: 'number',
    description: 'Jot & Chaigne / DAFx Feedback Delay Networks reverb (wet-only output)',
    examples: [
      'sin(hz:440) |> fdn(in:%,size:1,damping:0.1,decay:0.5) |> out(L:%,R:%)',
      'saw(hz:440) |> fdn(in:%,size:1.8,damping:0.6,decay:0.8) |> out(L:%,R:%)',
    ],
  },
  fdn2: {
    name: 'fdn2',
    parameters: [
      { name: 'in', type: 'number', description: 'The input signal', defaultValue: 0 },
      { name: 'size', type: 'number', description: 'Room size (0.1-2.0)', defaultValue: 1 },
      {
        name: 'damping',
        type: 'number',
        description: 'High-frequency damping (0-1)',
        defaultValue: 0.5,
      },
      { name: 'decay', type: 'number', description: 'Feedback decay (0-0.999)', defaultValue: 0.5 },
    ],
    returnType: 'number',
    description:
      'Improved FDN reverb with delay modulation, tilt EQ, diffusion, and early reflections (wet-only output)',
    examples: [
      'sin(hz:440) |> fdn2(in:%,size:1,damping:0.5,decay:0.5) |> out(L:%,R:%)',
      'saw(hz:440) |> fdn2(in:%,size:2,damping:0.6,decay:0.7) |> out(L:%,R:%)',
    ],
  },
  velvet: {
    name: 'velvet',
    parameters: [
      { name: 'in', type: 'number', description: 'The input signal', defaultValue: 0 },
      { name: 'size', type: 'number', description: 'Room size (0.1-2.0)', defaultValue: 1 },
      {
        name: 'damping',
        type: 'number',
        description: 'High-frequency damping (0-1)',
        defaultValue: 0.5,
      },
      { name: 'decay', type: 'number', description: 'Feedback decay (0-0.999)', defaultValue: 0.5 },
    ],
    returnType: 'number',
    description: 'Velvet noise reverb algorithm using sparse impulse sequences (wet-only output)',
    examples: [
      'sin(hz:440) |> velvet(in:%,size:1,damping:0.1,decay:0.5) |> out(L:%,R:%)',
      'saw(hz:440) |> velvet(in:%,size:1.9,damping:0.6,decay:0.8) |> out(L:%,R:%)',
    ],
  },
  lexicon: {
    name: 'lexicon',
    parameters: [
      { name: 'in', type: 'number', description: 'The input signal', defaultValue: 0 },
      {
        name: 'size',
        type: 'number',
        description: 'Reverb time/size (0.1-4.0)',
        defaultValue: 1.5,
      },
      {
        name: 'diffusion',
        type: 'number',
        description: 'Early reflections diffusion (0-0.9)',
        defaultValue: 0.5,
      },
      {
        name: 'damping',
        type: 'number',
        description: 'High-frequency damping (0-1)',
        defaultValue: 0.5,
      },
      {
        name: 'modulation',
        type: 'number',
        description: 'Chorus/shimmer depth (0-1)',
        defaultValue: 0.5,
      },
    ],
    returnType: 'number',
    description: 'Griesinger/Lexicon-style reverb with modulated delays and rich texture (wet-only output)',
    examples: [
      'sin(hz:440) |> lexicon(in:%,size:1.5,diffusion:0.625,damping:0.25,modulation:0.25) |> out(L:%,R:%)',
      'saw(hz:440) |> lexicon(in:%,size:3.2,diffusion:0.55,damping:0.7,modulation:0.8) |> out(L:%,R:%)',
    ],
  },
  spring: {
    name: 'spring',
    parameters: [
      { name: 'in', type: 'number', description: 'The input signal', defaultValue: 0 },
      { name: 'size', type: 'number', description: 'Spring length/size (0.1-2.0)', defaultValue: 1 },
      {
        name: 'damping',
        type: 'number',
        description: 'High-frequency damping (0-1)',
        defaultValue: 0.5,
      },
      { name: 'decay', type: 'number', description: 'Feedback decay (0-0.999)', defaultValue: 0.5 },
    ],
    returnType: 'number',
    description: 'Spring reverb algorithm with cross-coupled delay lines (wet-only output)',
    examples: [
      'sin(hz:440) |> spring(in:%,size:1,damping:0.5,decay:0.5) |> out(L:%,R:%)',
      'saw(hz:440) |> spring(in:%,size:1.5,damping:0.6,decay:0.7) |> out(L:%,R:%)',
    ],
  },
  out: {
    name: 'out',
    parameters: [
      { name: 'L', type: 'number', description: 'The left channel signal' },
      { name: 'R', type: 'number', description: 'The right channel signal' },
    ],
    returnType: 'void',
    description: 'Sends the signal to the left and right channels',
    examples: [
      'sin(hz:440) |> out(L:%,R:%)',
      'sin(hz:440) |> lp(in:%,cut:500) |> out(L:%,R:%)',
    ],
  },
  solo: {
    name: 'solo',
    parameters: [
      { name: 'L', type: 'number', description: 'The left channel signal' },
      { name: 'R', type: 'number', description: 'The right channel signal' },
    ],
    returnType: 'void',
    description:
      'Stops everything else going into out and only outputs its own signal to the actual output, still using post processing. Can be shortened to solo(L) to output the same to the Right channel like mono.',
    examples: [
      'sin(hz:440) |> solo(%)',
      'sin(hz:440) |> solo(L:%,R:%)',
      'sin(hz:440) |> lp(in:%,cut:500) |> solo(L:%,R:%)',
    ],
  },
  pan: {
    name: 'pan',
    parameters: [
      { name: 'in', type: 'number', description: 'The input signal', defaultValue: 0 },
      {
        name: 'pan',
        type: 'number',
        description: 'Pan position (-1 = fully left, 0 = center, 1 = fully right)',
        defaultValue: 0,
      },
    ],
    returnType: 'void',
    description: 'Pans the input signal between left and right channels',
    examples: [
      'sin(hz:440) |> pan(in:%,pan:0) |> out(L:%,R:%)',
      'sin(hz:440) |> pan(in:%,pan:lfosin(beat:1/4)) |> out(L:%,R:%)',
    ],
  },
  stereo: {
    name: 'stereo',
    parameters: [
      { name: 'in', type: 'number', description: 'The input signal', defaultValue: 0 },
      {
        name: 'separation',
        type: 'number',
        description: 'Delay time in milliseconds for the right channel to create stereo width',
        defaultValue: 10,
      },
    ],
    returnType: 'void',
    description: 'Creates a stereo effect by delaying the right channel',
    examples: [
      'sin(hz:440) |> stereo(in:%,separation:10) |> out(L:%,R:%)',
      'saw(hz:440) |> stereo(in:%,separation:20) |> out(L:%,R:%)',
    ],
  },
  widen: {
    name: 'widen',
    parameters: [
      { name: 'in', type: 'number', description: 'The input signal', defaultValue: 0 },
      {
        name: 'ms',
        type: 'number',
        description: 'Delay time in milliseconds for stereo widening of high frequencies',
        defaultValue: 10,
      },
      {
        name: 'cut',
        type: 'number',
        description: 'Cutoff frequency in Hz to split low and high frequencies',
        defaultValue: 200,
      },
    ],
    returnType: 'void',
    description:
      'Splits the signal into low and high frequencies, outputs low frequencies to center, and applies stereo widening to high frequencies',
    examples: [
      'sin(hz:440) |> widen(in:%,ms:10,cut:200) |> out(L:%,R:%)',
      'saw(hz:440) |> widen(in:%,ms:3,cut:200) |> out(L:%,R:%)',
    ],
  },
  seq: {
    name: 'seq',
    parameters: [
      { name: 'seq', type: 'string', description: 'The sequence to play' },
      { name: 'swing', type: 'number', description: 'Swing amount (0-1, default 0)' },
      { name: 'offset', type: 'number', description: 'Offset in seconds (default 0)' },
      { name: 'cb', type: 'function',
        description: 'The callback function (trig, velocity) or (trig, velocity, pitches) => {...}' },
    ],
    returnType: 'number',
    description: 'Returns the sequence of the given parameters',
    examples: [
      'seq("c2 e2 g2", cb:(trig,vel,pitches) => sin(hz:pitches[0],trig:trig)*vel |> out(L:%,R:%))',
      'seq("c2:1/4@0.8 e2:1/8@1 g2:1/4@0.6", cb:(trig,vel,pitches) => sin(hz:pitches[0],trig:trig)*vel |> out(L:%,R:%))',
    ],
  },
  pat: {
    name: 'pat',
    parameters: [
      { name: 'pat', type: 'string',
        description: 'The Mini-Notation pattern string (e.g. "bd*4", "< a b c d >", "c4.5")' },
      { name: 'swing', type: 'number', description: 'Swing amount (0-1, default 0)' },
      { name: 'offset', type: 'number', description: 'Offset in seconds (default 0)' },
      {
        name: 'seed',
        type: 'number',
        description: 'Seed for deterministic randomness in patterns with | (random choice)',
      },
      {
        name: 'variations',
        type: 'number',
        description: 'Number of variation cycles for random patterns (>= 1, default 1)',
      },
      { name: 'cb', type: 'function',
        description:
          'The callback function: (trig) => {...} or (trig, velocity) => {...} or (trig, velocity, pitches) => {...}' },
    ],
    returnType: 'number',
    description:
      'Plays a Mini-Notation pattern (Tidal Cycles / Strudel style) with callback for each event. Velocity can be specified with ".value" (e.g. "c4.3" or "[c3e3g3].4")',
    examples: [
      'pat("bd*4", cb:(trig) => sin(hz:60,trig:trig) |> out(L:%,R:%))',
      'pat("< c2 e2 g2 >", cb:(trig,vel,pitches) => sin(hz:pitches[0],trig:trig)*vel |> out(L:%,R:%))',
      'pat("c4.3 e4.5 g4.7", cb:(trig,vel) => sin(hz:440,trig:trig)*vel |> out(L:%,R:%))',
    ],
  },
  comp: {
    name: 'comp',
    parameters: [
      { name: 'in', type: 'number', description: 'The input signal', defaultValue: 0 },
      { name: 'threshold', type: 'number', description: 'The threshold in dB', defaultValue: -20 },
      { name: 'ratio', type: 'number', description: 'The compression ratio', defaultValue: 4 },
      {
        name: 'attack',
        type: 'number',
        description: 'The attack time in seconds',
        defaultValue: 0.01,
      },
      {
        name: 'release',
        type: 'number',
        description: 'The release time in seconds',
        defaultValue: 0.1,
      },
      { name: 'knee', type: 'number', description: 'The knee width in dB', defaultValue: 1 },
    ],
    returnType: 'number',
    description: 'Returns the compressed signal',
    deprecated: true,
  },
  compressor: {
    name: 'compressor',
    parameters: [
      { name: 'in', type: 'number', description: 'The input signal', defaultValue: 0 },
      { name: 'threshold', type: 'number', description: 'The threshold in dB', defaultValue: -20 },
      { name: 'ratio', type: 'number', description: 'The compression ratio', defaultValue: 4 },
      {
        name: 'attack',
        type: 'number',
        description: 'The attack time in seconds',
        defaultValue: 0.01,
      },
      {
        name: 'release',
        type: 'number',
        description: 'The release time in seconds',
        defaultValue: 0.1,
      },
      { name: 'knee', type: 'number', description: 'The knee width in dB', defaultValue: 1 },
    ],
    returnType: 'number',
    description: 'Returns the compressed signal',
    examples: [
      'sin(hz:440) |> compressor(in:%,threshold:-20,ratio:4,attack:0.01,release:0.1,knee:1) |> out(L:%,R:%)',
      'saw(hz:440) |> compressor(in:%,threshold:-12,ratio:4.5,attack:0.0001,release:0.003,knee:2) |> out(L:%,R:%)',
    ],
  },
  expander: {
    name: 'expander',
    parameters: [
      { name: 'in', type: 'number', description: 'The input signal', defaultValue: 0 },
      { name: 'threshold', type: 'number', description: 'The threshold in dB', defaultValue: -20 },
      { name: 'ratio', type: 'number', description: 'The expansion ratio', defaultValue: 4 },
      {
        name: 'attack',
        type: 'number',
        description: 'The attack time in seconds',
        defaultValue: 0.01,
      },
      {
        name: 'release',
        type: 'number',
        description: 'The release time in seconds',
        defaultValue: 0.1,
      },
      { name: 'knee', type: 'number', description: 'The knee width in dB', defaultValue: 1 },
    ],
    returnType: 'number',
    description: 'Returns the expanded signal',
    examples: [
      'sin(hz:440) |> expander(in:%,threshold:-20,ratio:4,attack:0.01,release:0.1,knee:1) |> out(L:%,R:%)',
      'saw(hz:440) |> expander(in:%,threshold:-15,ratio:3,attack:0.005,release:0.05,knee:2) |> out(L:%,R:%)',
    ],
  },
  gate: {
    name: 'gate',
    parameters: [
      { name: 'in', type: 'number', description: 'The input signal', defaultValue: 0 },
      { name: 'threshold', type: 'number', description: 'The threshold in dB', defaultValue: -20 },
      { name: 'ratio', type: 'number', description: 'The gate ratio', defaultValue: 10 },
      {
        name: 'attack',
        type: 'number',
        description: 'The attack time in seconds',
        defaultValue: 0.01,
      },
      {
        name: 'release',
        type: 'number',
        description: 'The release time in seconds',
        defaultValue: 0.1,
      },
      { name: 'knee', type: 'number', description: 'The knee width in dB', defaultValue: 1 },
    ],
    returnType: 'number',
    description: 'Returns the gated signal',
    examples: [
      'noise(seed:1234) |> gate(in:%,threshold:-20,ratio:10,attack:0.01,release:0.1,knee:1) |> out(L:%,R:%)',
      'sin(hz:440) |> gate(in:%,threshold:-30,ratio:20,attack:0.003,release:0.05,knee:2) |> out(L:%,R:%)',
    ],
  },
  limiter: {
    name: 'limiter',
    parameters: [{ name: 'in', type: 'number', description: 'The input signal', defaultValue: 0 }],
    returnType: 'number',
    description: 'Returns the limited signal with brickwall settings',
    examples: [
      'sin(hz:440)*2 |> limiter(in:%) |> out(L:%,R:%)',
      'saw(hz:440) |> compressor(in:%,threshold:-8,ratio:12) |> limiter(in:%) |> out(L:%,R:%)',
    ],
  },
  vocoder: {
    name: 'vocoder',
    parameters: [
      {
        name: 'carrier',
        type: 'number',
        description: 'The carrier signal (e.g., synth)',
        defaultValue: 0,
      },
      {
        name: 'modulator',
        type: 'number',
        description: 'The modulator signal (e.g., voice)',
        defaultValue: 0,
      },
      {
        name: 'bands',
        type: 'number',
        description: 'Number of frequency bands (4-32)',
        defaultValue: 16,
      },
      {
        name: 'formantShift',
        type: 'number',
        description: 'Formant shift factor (0.5-2.0)',
        defaultValue: 1.0,
      },
    ],
    returnType: 'number',
    description: 'Returns the vocoded signal by modulating the carrier with the modulator envelope',
    examples: [
      'vocoder(carrier:sin(hz:440),modulator:noise(seed:1234),bands:16,formantShift:1.0) |> out(L:%,R:%)',
      'vocoder(carrier:seq("c2 e2 g2",cb:(trig,vel,pitches)=>sin(hz:pitches[0],trig:trig)),modulator:noise(seed:1234),bands:25,formantShift:1.5) |> out(L:%,R:%)',
    ],
  },
  side: {
    name: 'side',
    parameters: [
      { name: 'in', type: 'number', description: 'The input signal' },
      { name: 'chain', type: 'number', description: 'The sidechain signal' },
      { name: 'threshold', type: 'number', description: 'The threshold in dB' },
      { name: 'ratio', type: 'number', description: 'The compression ratio' },
      { name: 'attack', type: 'number', description: 'The attack time in seconds' },
      { name: 'release', type: 'number', description: 'The release time in seconds' },
      { name: 'knee', type: 'number', description: 'The knee width in dB' },
    ],
    returnType: 'number',
    description: 'Returns the sidechain compressed signal',
    deprecated: true,
  },
  sidechain: {
    name: 'sidechain',
    parameters: [
      { name: 'in', type: 'number', description: 'The input signal' },
      { name: 'key', type: 'number', description: 'The sidechain signal' },
      { name: 'threshold', type: 'number', description: 'The threshold in dB' },
      { name: 'ratio', type: 'number', description: 'The compression ratio' },
      { name: 'attack', type: 'number', description: 'The attack time in seconds' },
      { name: 'release', type: 'number', description: 'The release time in seconds' },
      { name: 'knee', type: 'number', description: 'The knee width in dB' },
    ],
    returnType: 'number',
    description: 'Returns the sidechain compressed signal',
    examples: [
      'sin(hz:440) |> sidechain(in:%,key:beat(1/4),threshold:-20,ratio:5,attack:0.0001,release:0.35,knee:1) |> out(L:%,R:%)',
      'saw(hz:440) |> sidechain(in:%,key:beat(1/8),threshold:-12,ratio:5,attack:0.0008,release:0.15,knee:1) |> out(L:%,R:%)',
    ],
  },
  conv: {
    name: 'conv',
    parameters: [
      { name: 'in', type: 'number', description: 'The input signal' },
      { name: 'impulse', type: 'string', description: 'The impulse response file path' },
    ],
    returnType: 'number',
    description: 'Returns the convolved signal with the impulse response',
    examples: [
      'sin(hz:440) |> conv(in:%,impulse:"impulse.wav") |> out(L:%,R:%)',
      'saw(hz:440) |> conv(in:%,impulse:"room.wav") |> out(L:%,R:%)',
    ],
  },
  tanh: {
    name: 'tanh',
    parameters: [{ name: 'in', type: 'number', description: 'The input signal' }],
    returnType: 'number',
    description: 'Returns the hyperbolic tangent waveshaped signal',
    examples: [
      'sin(hz:440)*2 |> tanh(in:%) |> out(L:%,R:%)',
      'saw(hz:440) |> tanh(in:%*1.5) |> out(L:%,R:%)',
    ],
  },
  amplitude: {
    name: 'amplitude',
    parameters: [{ name: 'in', type: 'number', description: 'The input signal' }],
    returnType: 'number',
    description: 'Pass-through function that captures the signal for amplitude visualization',
    examples: [
      'sin(hz:440) |> amplitude(in:%) |> out(L:%,R:%)',
      'saw(hz:440) |> amplitude(in:%) |> out(L:%,R:%)',
    ],
  },
  waveform: {
    name: 'waveform',
    parameters: [{ name: 'in', type: 'number', description: 'The input signal' }],
    returnType: 'number',
    description: 'Pass-through function that captures the signal for waveform visualization',
    examples: [
      'sin(hz:440) |> waveform(in:%) |> out(L:%,R:%)',
      'saw(hz:440) |> waveform(in:%) |> out(L:%,R:%)',
    ],
  },
  metronome: {
    name: 'metronome',
    parameters: [],
    returnType: 'void',
    description: 'Plays a 4/4 metronome pattern',
    examples: [
      'metronome()',
    ],
  },
  level: {
    name: 'level',
    parameters: [{ name: 'in', type: 'number', description: 'The input signal' }],
    returnType: 'number',
    description: 'Pass-through function that captures the signal for level visualization (peak & RMS)',
    examples: [
      'sin(hz:440) |> level(in:%) |> out(L:%,R:%)',
      'saw(hz:440) |> level(in:%) |> out(L:%,R:%)',
    ],
  },
  atan: {
    name: 'atan',
    parameters: [{ name: 'in', type: 'number', description: 'The input signal' }],
    returnType: 'number',
    description: 'Returns the arctangent waveshaped signal',
    examples: [
      'sin(hz:440)*2 |> atan(in:%) |> out(L:%,R:%)',
      'saw(hz:440) |> atan(in:%*1.8) |> out(L:%,R:%)',
    ],
  },
  abs: {
    name: 'abs',
    parameters: [{ name: 'x', type: 'number', description: 'The input value' }],
    returnType: 'number',
    description: 'Returns the absolute value of x',
    examples: [
      'abs(-5)',
      'abs(sin(hz:440)) |> out(L:%,R:%)',
    ],
  },
  sqrt: {
    name: 'sqrt',
    parameters: [{ name: 'x', type: 'number', description: 'The input value' }],
    returnType: 'number',
    description: 'Returns the square root of x',
    examples: [
      'sqrt(16)',
      'sqrt(abs(sin(hz:440))) |> out(L:%,R:%)',
    ],
  },
  log: {
    name: 'log',
    parameters: [{ name: 'x', type: 'number', description: 'The input value' }],
    returnType: 'number',
    description: 'Returns the natural logarithm of x',
    examples: [
      'log(2.718)',
      'log(abs(sin(hz:440))+0.001) |> out(L:%,R:%)',
    ],
  },
  exp: {
    name: 'exp',
    parameters: [{ name: 'x', type: 'number', description: 'The input value' }],
    returnType: 'number',
    description: 'Returns e raised to the power of x',
    examples: [
      'exp(1)',
      'exp(sin(hz:1)*2) |> out(L:%,R:%)',
    ],
  },
  db: {
    name: 'db',
    parameters: [{ name: 'x', type: 'number', description: 'The dB value' }],
    returnType: 'number',
    description: 'Returns the linear gain multiplier for the given dB value (e.g., db(3) ≈ 1.4125, db(-3) ≈ 0.7079)',
    examples: [
      'sin(hz:440)*db(3) |> out(L:%,R:%)',
      'saw(hz:440)*db(-6) |> out(L:%,R:%)',
    ],
  },
  min: {
    name: 'min',
    parameters: [
      { name: 'a', type: 'number', description: 'First value' },
      { name: 'b', type: 'number', description: 'Second value' },
    ],
    returnType: 'number',
    description: 'Returns the minimum of a and b',
    examples: [
      'min(5,10)',
      'min(sin(hz:440),0.5) |> out(L:%,R:%)',
    ],
  },
  max: {
    name: 'max',
    parameters: [
      { name: 'a', type: 'number', description: 'First value' },
      { name: 'b', type: 'number', description: 'Second value' },
    ],
    returnType: 'number',
    description: 'Returns the maximum of a and b',
    examples: [
      'max(5,10)',
      'max(sin(hz:440),-0.5) |> out(L:%,R:%)',
    ],
  },
  clamp: {
    name: 'clamp',
    parameters: [
      { name: 'x', type: 'number', description: 'The value to clamp' },
      { name: 'min', type: 'number', description: 'Minimum value' },
      { name: 'max', type: 'number', description: 'Maximum value' },
    ],
    returnType: 'number',
    description: 'Clamps x between min and max',
    examples: [
      'clamp(sin(hz:440),-0.5,0.5) |> out(L:%,R:%)',
      'clamp(saw(hz:440)*2,-1,1) |> out(L:%,R:%)',
    ],
  },
  wrap: {
    name: 'wrap',
    parameters: [
      { name: 'x', type: 'number', description: 'The value to wrap' },
      { name: 'min', type: 'number', description: 'Minimum bound' },
      { name: 'max', type: 'number', description: 'Maximum bound' },
    ],
    returnType: 'number',
    description: 'Wraps x into the range [min, max)',
    examples: [
      'wrap(sin(hz:440)*5,0,1) |> out(L:%,R:%)',
      'wrap(phasor(hz:1)*10,0,5) |> out(L:%,R:%)',
    ],
  },
  floor: {
    name: 'floor',
    parameters: [{ name: 'x', type: 'number', description: 'The input value' }],
    returnType: 'number',
    description: 'Returns the largest integer less than or equal to x',
    examples: [
      'floor(3.7)',
      'floor(phasor(hz:1)*10) |> out(L:%,R:%)',
    ],
  },
  ceil: {
    name: 'ceil',
    parameters: [{ name: 'x', type: 'number', description: 'The input value' }],
    returnType: 'number',
    description: 'Returns the smallest integer greater than or equal to x',
    examples: [
      'ceil(3.2)',
      'ceil(phasor(hz:1)*10) |> out(L:%,R:%)',
    ],
  },
  fract: {
    name: 'fract',
    parameters: [{ name: 'x', type: 'number', description: 'The input value' }],
    returnType: 'number',
    description: 'Returns the fractional part of x (x - floor(x))',
    examples: [
      'fract(3.7)',
      'fract(phasor(hz:1)*10) |> out(L:%,R:%)',
    ],
  },
  sign: {
    name: 'sign',
    parameters: [{ name: 'x', type: 'number', description: 'The input value' }],
    returnType: 'number',
    description: 'Returns the sign of x (-1 if negative, 0 if zero, 1 if positive)',
    examples: [
      'sign(-5)',
      'sign(sin(hz:440)) |> out(L:%,R:%)',
    ],
  },
  soft: {
    name: 'soft',
    parameters: [{ name: 'in', type: 'number', description: 'The input signal' }],
    returnType: 'number',
    description: 'Returns the soft clipping distorted signal',
    examples: [
      'sin(hz:440)*2 |> soft(in:%) |> out(L:%,R:%)',
      'saw(hz:440) |> soft(in:%*1.5) |> out(L:%,R:%)',
    ],
  },
  asym: {
    name: 'asym',
    parameters: [{ name: 'in', type: 'number', description: 'The input signal' }],
    returnType: 'number',
    description: 'Returns the asymmetric distorted signal',
    examples: [
      'sin(hz:440)*2 |> asym(in:%) |> out(L:%,R:%)',
      'saw(hz:440) |> asym(in:%*3) |> out(L:%,R:%)',
    ],
  },
  select: {
    name: 'select',
    parameters: [
      { name: 'in1', type: 'number', description: 'The first input signal', defaultValue: 0 },
      { name: 'in2', type: 'number', description: 'The second input signal', defaultValue: 0 },
      {
        name: 'sign',
        type: 'number',
        description: 'The selector signal (<=0 selects in1, >0 selects in2)',
        defaultValue: 0,
      },
    ],
    returnType: 'number',
    description: 'Selects between two inputs based on a sign signal',
    examples: [
      'select(in1:sin(hz:440),in2:sin(hz:880),sign:lfosqr(beat:1/4)) |> out(L:%,R:%)',
      'select(in1:sin(hz:440),in2:tri(hz:440),sign:beat(1/2)) |> out(L:%,R:%)',
    ],
  },
  crossfade: {
    name: 'crossfade',
    parameters: [
      { name: 'in1', type: 'number', description: 'The first input signal', defaultValue: 0 },
      { name: 'in2', type: 'number', description: 'The second input signal', defaultValue: 0 },
      {
        name: 'amount',
        type: 'number',
        description: 'Crossfade amount (-1 = in1 only, 0 = equal mix, 1 = in2 only)',
        defaultValue: 0,
      },
    ],
    returnType: 'number',
    description: 'Crossfades between two inputs based on amount',
    examples: [
      'crossfade(in1:sin(hz:440),in2:sin(hz:880),amount:lfosin(beat:1/4)) |> out(L:%,R:%)',
      'crossfade(in1:sin(hz:440),in2:tri(hz:440),amount:0.5) |> out(L:%,R:%)',
    ],
  },
  swap: {
    name: 'swap',
    parameters: [
      { name: 'in1', type: 'number', description: 'The first input signal', defaultValue: 0 },
      { name: 'in2', type: 'number', description: 'The second input signal', defaultValue: 0 },
      {
        name: 'trig',
        type: 'number',
        description: 'Trigger signal (swaps inputs on each impulse)',
        defaultValue: 0,
      },
    ],
    returnType: 'number',
    description: 'Swaps between two inputs on every trigger impulse',
    examples: [
      'swap(in1:sin(hz:440),in2:sin(hz:880),trig:beat(1/4)) |> out(L:%,R:%)',
      'swap(in1:sin(hz:440),in2:tri(hz:440),trig:beat(1/8)) |> out(L:%,R:%)',
    ],
  },
  bitcrusher: {
    name: 'bitcrusher',
    parameters: [
      { name: 'in', type: 'number', description: 'The input signal', defaultValue: 0 },
      {
        name: 'bits',
        type: 'number',
        description: 'The bit depth (e.g., 8, 4, 2)',
        defaultValue: 8,
      },
    ],
    returnType: 'number',
    description: 'Returns the bitcrushed signal with reduced bit depth',
    examples: [
      'sin(hz:440) |> bitcrusher(in:%,bits:8) |> out(L:%,R:%)',
      'saw(hz:440) |> bitcrusher(in:%,bits:4) |> out(L:%,R:%)',
    ],
  },
  post: {
    name: 'post',
    parameters: [
      {
        name: 'callback',
        type: 'function',
        description: 'Post-processing function (L,R)->out(...)',
      },
    ],
    returnType: 'void',
    description: 'Applies post-processing to the master output',
    examples: [
      'post((L,R) => out(L:limiter(L),R:limiter(R)))',
      'post((L,R) => out(L:compressor(L,threshold:-8,ratio:12),R:compressor(R,threshold:-8,ratio:12)))',
    ],
  },
  sample: {
    name: 'sample',
    parameters: [
      { name: 'src', type: 'string', description: 'Sample file path' },
      { name: 'trig', type: 'number', description: 'Trigger sample playback', defaultValue: 0 },
      {
        name: 'speed',
        type: 'number',
        description: 'Playback speed (1.0 = normal, 2.0 = double speed, 0.5 = half speed)',
        defaultValue: 1,
      },
      {
        name: 'offset',
        type: 'number',
        description: 'Start position (0 = start, 1 = end, negative wraps: -0.1 = 0.9)',
        defaultValue: 0,
      },
    ],
    returnType: 'number',
    description:
      'Plays back a loaded audio sample. Sample is automatically normalized to -1..1 range for consistent levels.',
    examples: [
      'beat(1/4) |> (trig => sample(src:"kick.wav",trig:trig) |> out(L:%,R:%))',
      'beat(1/8) |> (trig => sample(src:"snare.wav",trig:trig,speed:1.2) |> out(L:%,R:%))',
    ],
  },
  freesound: {
    name: 'freesound',
    parameters: [
      { name: 'id', type: 'number', description: 'Freesound sample ID' },
      { name: 'trig', type: 'number', description: 'Trigger sample playback' },
      {
        name: 'speed',
        type: 'number',
        description: 'Playback speed (1.0 = normal, 2.0 = double speed, 0.5 = half speed)',
      },
      {
        name: 'offset',
        type: 'number',
        description: 'Start position (0 = start, 1 = end, negative wraps: -0.1 = 0.9)',
      },
    ],
    returnType: 'number',
    description:
      'Plays back a sample from Freesound.org. Sample is automatically normalized to -1..1 range for consistent levels.',
    examples: [
      'beat(1/4) |> (trig => freesound(id:793920,trig,speed:1.0) |> out(L:%,R:%))',
      'beat(1/8) |> (trig => freesound(id:809135,trig,speed:1.2) |> out(L:%,R:%))',
    ],
  },
  sam: {
    name: 'sam',
    parameters: [
      { name: 'speak', type: 'string', description: 'Text to speak' },
      { name: 'trig', type: 'number', description: 'Trigger speech playback' },
      { name: 'speed', type: 'number', description: 'Speech speed (0-1, default 0.5)' },
      { name: 'pitch', type: 'number', description: 'Speech pitch (0-1, default 0.5)' },
      { name: 'throat', type: 'number', description: 'Throat parameter (0-1, default 0.5)' },
      { name: 'mouth', type: 'number', description: 'Mouth parameter (0-1, default 0.5)' },
      {
        name: 'playback',
        type: 'number',
        description: 'Playback speed (1.0 = normal, 2.0 = double speed, 0.5 = half speed)',
      },
      {
        name: 'offset',
        type: 'number',
        description: 'Start position (0 = start, 1 = end, negative wraps: -0.1 = 0.9)',
      },
    ],
    returnType: 'number',
    description:
      'Text-to-speech using SAM (Software Automatic Mouth). Audio is automatically normalized to -1..1 range for consistent levels.',
    examples: [
      'sam(speak:"hello",trig:beat(1/4),speed:0.5,pitch:0.5,throat:0.5,mouth:0.5) |> out(L:%,R:%)',
      'sam(speak:"loopmaster",trig:[1,0].walk(1/4),speed:0.75,pitch:0.5) |> out(L:%,R:%)',
    ],
  },
  espeak: {
    name: 'espeak',
    parameters: [
      { name: 'text', type: 'string', description: 'Text to speak' },
      {
        name: 'variant',
        type: 'string',
        description: 'Voice variant (default: "m1")',
      },
      {
        name: 'speed',
        type: 'number',
        description: 'Speech speed (0-1, default 0.5)',
      },
      {
        name: 'pitch',
        type: 'number',
        description: 'Speech pitch (0-1,default 0.5)',
      },
      {
        name: 'rate',
        type: 'number',
        description: 'Playback rate (1.0 = normal, 2.0 = double rate, 0.5 = half rate)',
      },
      {
        name: 'offset',
        type: 'number',
        description: 'Start position (0 = start, 1 = end, negative wraps: -0.1 = 0.9)',
      },
      { name: 'trig', type: 'number', description: 'Trigger speech playback' },
    ],
    returnType: 'number',
    description: 'Text-to-speech using espeak. Audio is automatically normalized to -1..1 range for consistent levels.',
    examples: [
      'espeak(text:"hello",variant:"m1",speed:0.5,pitch:0.5,rate:1,trig:beat(1/4)) |> out(L:%,R:%)',
      'espeak(text:"loopmaster",variant:"m7",speed:0.75,pitch:0.0,rate:0.95,trig:[1,0].walk(1/4)) |> out(L:%,R:%)',
    ],
  },
  pitchshift: {
    name: 'pitchshift',
    parameters: [
      { name: 'in', type: 'number', description: 'The input signal', defaultValue: 0 },
      {
        name: 'ratio',
        type: 'number',
        description: 'Pitch shift ratio (0.5 = octave down, 2 = octave up, 1 = same)',
        defaultValue: 1,
      },
    ],
    returnType: 'number',
    description: 'High-quality pitch shifter using overlap-add grain synthesis (changes pitch without changing speed)',
    examples: [
      'sin(hz:440) |> pitchshift(in:%,ratio:0.5) |> out(L:%,R:%)',
      'sin(hz:440) |> pitchshift(in:%,ratio:2) |> out(L:%,R:%)',
    ],
  },
  slicer: {
    name: 'slicer',
    parameters: [
      { name: 'src', type: 'string', description: 'Sample file path' },
      { name: 'trig', type: 'number', description: 'Trigger slice playback' },
      {
        name: 'speed',
        type: 'number',
        description: 'Playback speed (1.0 = normal, 2.0 = double speed, 0.5 = half speed)',
      },
      {
        name: 'offset',
        type: 'number',
        description: 'Slice selection (-1 = first slice, 0 = middle slice, 1 = last slice)',
      },
      {
        name: 'threshold',
        type: 'number',
        description: 'Onset detection sensitivity (0.00001-2.0, default 0.5). Lower = more slices detected.',
      },
    ],
    returnType: 'number',
    description:
      'Plays back audio sliced by onset detection. Sample is normalized to -1..1, then analyzed for energy increases (transients/beats). The offset parameter selects which slice to play.',
    examples: [
      'beat(1/16) |> (trig => slicer(src:"loop.wav",trig:trig,offset:0,threshold:0.5) |> out(L:%,R:%))',
      'beat(1/8) |> (trig => slicer(src:"drumloop.wav",trig:trig,offset:[1].walk(1/16),threshold:0.3) |> out(L:%,R:%))',
    ],
  },
  freesoundslicer: {
    name: 'freesoundslicer',
    parameters: [
      { name: 'id', type: 'number', description: 'Freesound sample ID' },
      { name: 'trig', type: 'number', description: 'Trigger slice playback' },
      {
        name: 'speed',
        type: 'number',
        description: 'Playback speed (1.0 = normal, 2.0 = double speed, 0.5 = half speed)',
      },
      {
        name: 'offset',
        type: 'number',
        description: 'Slice selection (-1 = first slice, 0 = middle slice, 1 = last slice)',
      },
      {
        name: 'threshold',
        type: 'number',
        description: 'Onset detection sensitivity (0.00001-2.0, default 0.5). Lower = more slices detected.',
      },
    ],
    returnType: 'number',
    description:
      'Plays back audio from Freesound.org sliced by onset detection. Sample is normalized to -1..1, then analyzed for energy increases (transients/beats). The offset parameter selects which slice to play.',
    examples: [
      'beat(1/16) |> (trig => freesoundslicer(id:809135,trig,offset:0,threshold:0.5) |> out(L:%,R:%))',
      'beat(1/8) |> (trig => freesoundslicer(id:809135,trig,offset:[0.7].walk(1/16),threshold:0.3) |> out(L:%,R:%))',
    ],
  },
  mtof: {
    name: 'mtof',
    parameters: [{ name: 'note', type: 'number', description: 'MIDI note number (0-127)' }],
    returnType: 'number',
    description: 'Converts a MIDI note number to frequency in Hz (A4 = 440 Hz)',
    examples: [
      'sin(hz:mtof(60)) |> out(L:%,R:%)',
      'sin(hz:mtof(69)) |> out(L:%,R:%)',
      'beat(1/4) |> (trig => sin(hz:mtof(60),trig:trig) |> out(L:%,R:%))',
    ],
  },
  transpose: {
    name: 'transpose',
    parameters: [{ name: 'semitones', type: 'number', description: 'Number of semitones to transpose' }],
    returnType: 'number',
    description:
      'Returns a multiplier to transpose a frequency by the given number of semitones (e.g., 440*transpose(-2))',
    examples: [
      'sin(hz:440*transpose(7)) |> out(L:%,R:%)',
      'sin(hz:440*transpose(-2)) |> out(L:%,R:%)',
    ],
  },
  karplus: {
    name: 'karplus',
    parameters: [
      { name: 'hz', type: 'number', description: 'The frequency in Hz', defaultValue: 440 },
      { name: 'trig', type: 'number', description: 'Trigger pluck', defaultValue: 0 },
      {
        name: 'damping',
        type: 'number',
        description: 'Damping factor (0.95-0.9999, higher = longer sustain)',
        defaultValue: 0.998,
      },
      {
        name: 'feedback',
        type: 'number',
        description: 'Feedback amount (0.0-1.0, 0.5 = balanced averaging)',
        defaultValue: 0.5,
      },
    ],
    returnType: 'number',
    description: 'Karplus-Strong string synthesis - generates plucked string sounds using filtered delay feedback',
    examples: [
      'karplus(hz:440,trig:beat(1/4),damping:0.998,feedback:0.5) |> out(L:%,R:%)',
      'karplus(hz:"c2"*1,trig:beat(1/8),damping:0.9999,feedback:0.74) |> out(L:%,R:%)',
    ],
  },
  dc: {
    name: 'dc',
    parameters: [{ name: 'in', type: 'number', description: 'The input signal', defaultValue: 0 }],
    returnType: 'number',
    description: 'Removes DC offset from the signal using a first-order high-pass filter',
    examples: [
      'sin(hz:440) |> dc(in:%) |> out(L:%,R:%)',
      'saw(hz:440) |> dc(in:%) |> out(L:%,R:%)',
    ],
  },
  onepole: {
    name: 'onepole',
    parameters: [
      { name: 'in', type: 'number', description: 'The input signal', defaultValue: 0 },
      {
        name: 'cut',
        type: 'number',
        description: 'Cutoff frequency in Hz (20 to Nyquist)',
        defaultValue: 1000,
      },
    ],
    returnType: 'number',
    description: 'One-pole low-pass filter with cutoff frequency control',
    examples: [
      'sin(hz:440) |> onepole(in:%,cut:500) |> out(L:%,R:%)',
      'saw(hz:440) |> onepole(in:%,cut:1000) |> out(L:%,R:%)',
    ],
  },
  slew: {
    name: 'slew',
    parameters: [
      { name: 'in', type: 'number', description: 'The input signal', defaultValue: 0 },
      {
        name: 'up',
        type: 'number',
        description: 'Slew rate per sample (rise and fall rate if down not specified)',
        defaultValue: 0.001,
      },
      {
        name: 'down',
        type: 'number',
        description: 'Separate fall rate per sample (defaults to up if not specified)',
        defaultValue: -1,
      },
      {
        name: 'exponent',
        type: 'number',
        description: 'Curve exponent (1.0 = linear, >1.0 = exponential, <1.0 = logarithmic)',
        defaultValue: 1.0,
      },
    ],
    returnType: 'number',
    description: 'Slew rate limiter that smoothly follows the input signal at a controlled rate',
    examples: [
      '"c4 c5 c6".walk(1/16) |> slew(in:%,up:0.001) |> (hz => sin(hz:hz) |> out(L:%,R:%))',
      'beat(1/8) |> slew(in:%,up:0.001,exponent:2) |> sin(hz:440*%,trig:beat(1/8)) |> out(L:%,R:%)',
    ],
  },
  sah: {
    name: 'sah',
    parameters: [
      { name: 'in', type: 'number', description: 'The input signal', defaultValue: 0 },
      {
        name: 'trig',
        type: 'number',
        description: 'Trigger signal (samples input when trig > 0)',
        defaultValue: 0,
      },
    ],
    returnType: 'number',
    description: 'Sample and hold - captures and holds the input value when the trigger signal is positive',
    examples: [
      'sin(hz:440) |> sah(in:%,trig:beat(1/4)) |> out(L:%,R:%)',
      'lfosin(beat:1/8) |> sah(in:%,trig:beat(1/4)) |> (v => sin(hz:440+v*200) |> out(L:%,R:%))',
    ],
  },
  envfollow: {
    name: 'envfollow',
    parameters: [
      { name: 'in', type: 'number', description: 'The input signal', defaultValue: 0 },
      {
        name: 'attack',
        type: 'number',
        description: 'The attack time in seconds',
        defaultValue: 0.01,
      },
      {
        name: 'release',
        type: 'number',
        description: 'The release time in seconds',
        defaultValue: 0.1,
      },
    ],
    returnType: 'number',
    description: 'Envelope follower that tracks the amplitude of the input signal',
    examples: [
      'sin(hz:440) |> envfollow(in:%,attack:0.01,release:0.1) |> (v => sin(hz:440+v*200) |> out(L:%,R:%))',
      'saw(hz:440) |> envfollow(in:%,attack:0.0001,release:0.15) |> (v => lp(in:saw(hz:440),cut:500+v*1000) |> out(L:%,R:%))',
    ],
  },
  '.walk': {
    name: '.walk',
    parameters: [
      {
        name: 'beat',
        type: 'number',
        description: 'Beat interval (e.g., 1/4, 1/8, 1/16). Required.',
      },
      { name: 'offset', type: 'number', description: 'Offset in seconds. Optional.' },
      { name: 'swing', type: 'number', description: 'Swing amount (0-1). Optional.' },
    ],
    returnType: 'number',
    description:
      'Sequentially walks through an array at a specified beat interval. Advances to the next value on each beat.',
    examples: [
      '"c4 c5 c6".walk(1/16) |> (hz => sin(hz:hz) |> out(L:%,R:%))',
      '"c2 c#3 g3".walk(1/8,swing:0.1) |> (hz => sin(hz:hz,trig:beat(1/8)) |> out(L:%,R:%))',
    ],
  },
  '.slide': {
    name: '.slide',
    parameters: [
      { name: 'beat', type: 'number', description: 'Beat interval (e.g., 1/4, 1/8). Required.' },
      { name: 'offset', type: 'number', description: 'Offset in seconds. Optional.' },
      {
        name: 'exponent',
        type: 'number',
        description: 'Curve exponent for interpolation (1=linear, 2=quadratic, etc.). Optional, defaults to 1.',
      },
      { name: 'swing', type: 'number', description: 'Swing amount (0-1). Optional.' },
    ],
    returnType: 'number',
    description:
      'Interpolates (slides) between array values at a specified beat interval. Smoothly transitions from one value to the next.',
    examples: [
      '"c4 c5 c6".slide(1/16) |> (hz => sin(hz:hz) |> out(L:%,R:%))',
      '"c2 c#3 g3".slide(1/8,exponent:2) |> (hz => sin(hz:hz) |> out(L:%,R:%))',
    ],
  },
  '.adv': {
    name: '.adv',
    parameters: [
      { name: 'trig', type: 'number', description: 'Trigger signal (0 or 1). Required.' },
    ],
    returnType: 'number',
    description: 'Advances through an array on trigger. Moves to the next value when trigger goes from 0 to 1.',
    deprecated: true,
  },
  '.step': {
    name: '.step',
    parameters: [
      { name: 'on', type: 'number', description: 'Trigger signal (0 or 1). Required.' },
      { name: 'offset', type: 'number', description: 'Starting offset index in the array. Optional, defaults to 0.' },
      { name: 'trig', type: 'number',
        description:
          'Reset trigger signal (0 or 1). When it receives an impulse, resets step to the offset. Optional.' },
    ],
    returnType: 'number',
    description: 'Advances through an array on trigger. Moves to the next value when trigger goes from 0 to 1.',
    examples: [
      '"c4 c5 c6".step(on:beat(1/16)) |> (hz => sin(hz:hz,trig:beat(1/16)) |> out(L:%,R:%))',
      '"c2 c#3 g3".step(on:beat(1/8),offset:1,trig:beat(1/2)) |> (hz => sin(hz:hz,trig:beat(1/8)) |> out(L:%,R:%))',
    ],
  },
  '.advslide': {
    name: '.advslide',
    parameters: [
      { name: 'trig', type: 'number', description: 'Trigger signal (0 or 1). Required.' },
      {
        name: 'exponent',
        type: 'number',
        description: 'Curve exponent for interpolation. Optional, defaults to 1.',
      },
    ],
    deprecated: true,
    returnType: 'number',
    description:
      'Advances through an array on trigger and slides to the next value. Combines .adv() with interpolation.',
  },
  '.rand': {
    name: '.rand',
    parameters: [
      { name: 'trig', type: 'number', description: 'Trigger signal (0 or 1). Required.' },
    ],
    returnType: 'number',
    description:
      'Randomly picks a value from an array on trigger. Selects a random element when trigger goes from 0 to 1.',
    examples: [
      '"c4 c5 c6".rand(trig:grain(0.5)) |> (hz => sin(hz:hz,trig:grain(0.5)) |> out(L:%,R:%))',
      '"c2 c#3 g3".rand(trig:beat(1/8)) |> (hz => sin(hz:hz,trig:beat(1/8)) |> out(L:%,R:%))',
    ],
  },
  '.randslide': {
    name: '.randslide',
    parameters: [
      { name: 'trig', type: 'number', description: 'Trigger signal (0 or 1). Required.' },
      {
        name: 'exponent',
        type: 'number',
        description: 'Curve exponent for interpolation. Optional, defaults to 1.',
      },
    ],
    deprecated: true,
    returnType: 'number',
    description:
      'Randomly picks a value from an array on trigger and slides to it. Combines .rand() with interpolation.',
  },
  '.randbeat': {
    name: '.randbeat',
    parameters: [
      {
        name: 'beat',
        type: 'number',
        description: 'Beat interval (e.g., 1/4, 1/8). Optional, defaults to 1/4.',
      },
    ],
    deprecated: true,
    returnType: 'number',
    description:
      'Randomly picks a value from an array on beat intervals. Uses beat-synced triggers (defaults to 1/4 if not specified).',
  },
  '.randbeatslide': {
    name: '.randbeatslide',
    parameters: [
      {
        name: 'beat',
        type: 'number',
        description: 'Beat interval (e.g., 1/4, 1/8). Optional, defaults to 1/4.',
      },
      {
        name: 'exponent',
        type: 'number',
        description: 'Curve exponent for interpolation. Optional, defaults to 1.',
      },
    ],
    deprecated: true,
    returnType: 'number',
    description:
      'Randomly picks a value from an array on beat intervals and slides to it. Combines .randbeat() with interpolation.',
  },
  '.pick': {
    name: '.pick',
    parameters: [
      {
        name: 'el',
        type: 'number',
        description: 'Element selector value (-1 to 1). -1 selects first element, 0 selects middle, 1 selects last.',
      },
    ],
    returnType: 'number',
    description:
      'Picks the nearest element from an array based on a value from -1 to 1. Maps the array length to the -1..1 range and selects the nearest element.',
    examples: [
      '"c4 c5 c6".pick(el:lfosin(beat:1/4)) |> (hz => sin(hz:hz) |> out(L:%,R:%))',
      '"c2 c#3 g3".pick(el:0) |> (hz => sin(hz:hz) |> out(L:%,R:%))',
    ],
  },
  '.sum': {
    name: '.sum',
    parameters: [],
    returnType: 'number',
    description: 'Sums all elements in the array',
    examples: [
      '"c2 c#3 g3".sum() |> (hz => sin(hz:hz) |> out(L:%,R:%))',
      'sin(hz:"c2c#3g3".sum()) |> out(L:%,R:%)',
    ],
  },
  '.map': {
    name: '.map',
    parameters: [
      {
        name: 'fn',
        type: 'function',
        description: 'Mapping function that transforms each element (e.g., v -> fn(v))',
      },
    ],
    returnType: 'number',
    description:
      'Maps each element of the array through a function. Returns an array that can be used in expressions. Example: "c2c#3g3".map(hz -> sin(hz:hz*2))',
    examples: [
      '"c2c#3g3".map(hz => sin(hz:hz*2)) |> out(L:%,R:%)',
      '"c4 e4 g4".map(hz => sin(hz:hz,trig:beat(1/4))) |> out(L:%,R:%)',
    ],
  },
  fm: {
    name: 'fm',
    parameters: [
      { name: 'op1', type: 'number', description: 'Carrier frequency in Hz' },
      { name: 'op2', type: 'number', description: 'Modulator frequency in Hz' },
      {
        name: 'index',
        type: 'number',
        description: 'Modulation depth (0-1.5 typical)',
        defaultValue: 1,
      },
      {
        name: 'feedback',
        type: 'number',
        description: 'Internal modulation feedback amount (0-1)',
        defaultValue: 0.5,
      },
      { name: 'trig', type: 'number', description: 'Trigger phase reset', defaultValue: 0 },
    ],
    returnType: 'number',
    description: 'FM oscillator with feedback & modulation control',
    examples: [
      'fm(op1:440,op2:4500,index:4,feedback:0.99,trig:beat(1/4)) |> out(L:%,R:%)',
      'fm(op1:"f2 a#2 d3 f3 d3 a3".walk(1/2)*4,op2:4500,index:4,feedback:0.99,trig:[1,0].walk(1/48)) |> out(L:%,R:%)',
    ],
  },
  arpodyssey: {
    name: 'arpodyssey',
    parameters: [
      { name: 'hz', type: 'number', description: 'Base frequency in Hz' },
      { name: 'v1', type: 'number', description: 'VCO1 frequency multiplier' },
      { name: 'v2', type: 'number', description: 'VCO2 frequency multiplier' },
      { name: 'mix', type: 'number', description: 'VCO mix (0-1)' },
      { name: 'cut', type: 'number', description: 'Filter cutoff frequency in Hz' },
      { name: 'q', type: 'number', description: 'Filter Q factor' },
      { name: 'lfo', type: 'number', description: 'LFO frequency multiplier' },
    ],
    returnType: 'number',
    description: 'Arp Odyssey-style synthesizer with dual VCOs and SVF low-pass filter',
    examples: [
      'arpodyssey(hz:440,v1:1,v2:1.02,mix:0.5,cut:500,q:0.7,lfo:0.3) |> out(L:%,R:%)',
      'arpodyssey(hz:"c3 e3 g3 b3".walk(1/8),v1:1.2,v2:0.8,mix:0.6,cut:1800,q:0.7,lfo:0.25) |> out(L:%,R:%)',
    ],
  },
  ms20: {
    name: 'ms20',
    parameters: [
      { name: 'hz', type: 'number', description: 'Base frequency in Hz' },
      { name: 'v1', type: 'number', description: 'VCO1 frequency multiplier' },
      { name: 'v2', type: 'number', description: 'VCO2 frequency multiplier' },
      { name: 'mix', type: 'number', description: 'VCO mix (0-1)' },
      { name: 'cut1', type: 'number', description: 'High-pass filter cutoff frequency in Hz' },
      { name: 'q1', type: 'number', description: 'High-pass filter Q factor' },
      { name: 'cut2', type: 'number', description: 'Low-pass filter cutoff frequency in Hz' },
      { name: 'q2', type: 'number', description: 'Low-pass filter Q factor' },
      { name: 'drive', type: 'number', description: 'Drive amount for saturation' },
    ],
    returnType: 'number',
    description: 'MS-20-style synthesizer with dual VCOs, high-pass and low-pass filters, and saturation',
    examples: [
      'ms20(hz:440,v1:1,v2:1,mix:0.5,cut1:200,q1:1,cut2:1000,q2:1,drive:1) |> out(L:%,R:%)',
      'ms20(hz:"c2"*1,v1:1,v2:2,mix:0.5,cut1:100,q1:1,cut2:500,q2:1,drive:2) |> out(L:%,R:%)',
    ],
  },
  minimoog: {
    name: 'minimoog',
    parameters: [
      { name: 'hz', type: 'number', description: 'Base frequency in Hz' },
      { name: 'v1', type: 'number', description: 'Oscillator 1 frequency multiplier' },
      { name: 'v2', type: 'number', description: 'Oscillator 2 frequency multiplier' },
      { name: 'v3', type: 'number', description: 'Oscillator 3 frequency multiplier' },
      { name: 'mix', type: 'number', description: 'Oscillator mix amount' },
      { name: 'cut', type: 'number', description: 'Moog filter cutoff frequency in Hz' },
      { name: 'q', type: 'number', description: 'Moog filter Q factor' },
    ],
    returnType: 'number',
    description: 'Minimoog-style synthesizer with three oscillators and Moog ladder filter',
    examples: [
      'minimoog(hz:440,v1:1,v2:1,v3:1,mix:1,cut:1000,q:0.5) |> out(L:%,R:%)',
      'minimoog(hz:"c2"*1,v1:1,v2:2,v3:0.5,mix:1,cut:500,q:0.7) |> out(L:%,R:%)',
    ],
  },
  dx7_1: {
    name: 'dx7_1',
    parameters: [
      { name: 'hz', type: 'number', description: 'Base frequency in Hz' },
      { name: 'r1', type: 'number', description: 'Operator 1 frequency ratio', defaultValue: 0 },
      { name: 'r2', type: 'number', description: 'Operator 2 frequency ratio', defaultValue: 0 },
      { name: 'r3', type: 'number', description: 'Operator 3 frequency ratio', defaultValue: 0 },
      { name: 'r4', type: 'number', description: 'Operator 4 frequency ratio', defaultValue: 0 },
      { name: 'r5', type: 'number', description: 'Operator 5 frequency ratio', defaultValue: 0 },
      { name: 'r6', type: 'number', description: 'Operator 6 frequency ratio', defaultValue: 0 },
      { name: 'm1', type: 'number', description: 'Modulation index 1', defaultValue: 1 },
      { name: 'm2', type: 'number', description: 'Modulation index 2', defaultValue: 1 },
      { name: 'm3', type: 'number', description: 'Modulation index 3', defaultValue: 1 },
      { name: 'm4', type: 'number', description: 'Modulation index 4', defaultValue: 1 },
      { name: 'm5', type: 'number', description: 'Modulation index 5', defaultValue: 1 },
    ],
    returnType: 'number',
    description: 'DX7 algorithm 1: 6-operator FM synthesis with serial modulation chain',
  },
  dx7_2: {
    name: 'dx7_2',
    parameters: [
      { name: 'hz', type: 'number', description: 'Base frequency in Hz' },
      { name: 'r1', type: 'number', description: 'Operator 1 frequency ratio', defaultValue: 0 },
      { name: 'r2', type: 'number', description: 'Operator 2 frequency ratio', defaultValue: 0 },
      { name: 'r3', type: 'number', description: 'Operator 3 frequency ratio', defaultValue: 0 },
      { name: 'r4', type: 'number', description: 'Operator 4 frequency ratio', defaultValue: 0 },
      { name: 'r5', type: 'number', description: 'Operator 5 frequency ratio', defaultValue: 0 },
      { name: 'r6', type: 'number', description: 'Operator 6 frequency ratio', defaultValue: 0 },
      { name: 'm1', type: 'number', description: 'Modulation index 1', defaultValue: 1 },
      { name: 'm2', type: 'number', description: 'Modulation index 2', defaultValue: 1 },
      { name: 'm3', type: 'number', description: 'Modulation index 3', defaultValue: 1 },
      { name: 'm4', type: 'number', description: 'Modulation index 4', defaultValue: 1 },
      { name: 'm5', type: 'number', description: 'Modulation index 5', defaultValue: 1 },
    ],
    returnType: 'number',
    description: 'DX7 algorithm 2: 6-operator FM synthesis with parallel output',
  },
  dx7_3: {
    name: 'dx7_3',
    parameters: [
      { name: 'hz', type: 'number', description: 'Base frequency in Hz' },
      { name: 'r1', type: 'number', description: 'Operator 1 frequency ratio', defaultValue: 0 },
      { name: 'r2', type: 'number', description: 'Operator 2 frequency ratio', defaultValue: 0 },
      { name: 'r3', type: 'number', description: 'Operator 3 frequency ratio', defaultValue: 0 },
      { name: 'r4', type: 'number', description: 'Operator 4 frequency ratio', defaultValue: 0 },
      { name: 'r5', type: 'number', description: 'Operator 5 frequency ratio', defaultValue: 0 },
      { name: 'r6', type: 'number', description: 'Operator 6 frequency ratio', defaultValue: 0 },
      { name: 'm1', type: 'number', description: 'Modulation index 1', defaultValue: 1 },
      { name: 'm2', type: 'number', description: 'Modulation index 2', defaultValue: 1 },
      { name: 'm3', type: 'number', description: 'Modulation index 3', defaultValue: 1 },
      { name: 'm4', type: 'number', description: 'Modulation index 4', defaultValue: 1 },
      { name: 'm5', type: 'number', description: 'Modulation index 5', defaultValue: 1 },
    ],
    returnType: 'number',
    description: 'DX7 algorithm 3: 6-operator FM synthesis with mixed parallel/serial structure',
  },
  dx7_4: {
    name: 'dx7_4',
    parameters: [
      { name: 'hz', type: 'number', description: 'Base frequency in Hz' },
      { name: 'r1', type: 'number', description: 'Operator 1 frequency ratio', defaultValue: 0 },
      { name: 'r2', type: 'number', description: 'Operator 2 frequency ratio', defaultValue: 0 },
      { name: 'r3', type: 'number', description: 'Operator 3 frequency ratio', defaultValue: 0 },
      { name: 'r4', type: 'number', description: 'Operator 4 frequency ratio', defaultValue: 0 },
      { name: 'r5', type: 'number', description: 'Operator 5 frequency ratio', defaultValue: 0 },
      { name: 'r6', type: 'number', description: 'Operator 6 frequency ratio', defaultValue: 0 },
      { name: 'm1', type: 'number', description: 'Modulation index 1', defaultValue: 1 },
      { name: 'm2', type: 'number', description: 'Modulation index 2', defaultValue: 1 },
      { name: 'm3', type: 'number', description: 'Modulation index 3', defaultValue: 1 },
      { name: 'm4', type: 'number', description: 'Modulation index 4', defaultValue: 1 },
      { name: 'm5', type: 'number', description: 'Modulation index 5', defaultValue: 1 },
    ],
    returnType: 'number',
    description: 'DX7 algorithm 4: 6-operator FM synthesis with branching modulation',
  },
  dx7_5: {
    name: 'dx7_5',
    parameters: [
      { name: 'hz', type: 'number', description: 'Base frequency in Hz' },
      { name: 'r1', type: 'number', description: 'Operator 1 frequency ratio', defaultValue: 0 },
      { name: 'r2', type: 'number', description: 'Operator 2 frequency ratio', defaultValue: 0 },
      { name: 'r3', type: 'number', description: 'Operator 3 frequency ratio', defaultValue: 0 },
      { name: 'r4', type: 'number', description: 'Operator 4 frequency ratio', defaultValue: 0 },
      { name: 'r5', type: 'number', description: 'Operator 5 frequency ratio', defaultValue: 0 },
      { name: 'r6', type: 'number', description: 'Operator 6 frequency ratio', defaultValue: 0 },
      { name: 'm1', type: 'number', description: 'Modulation index 1', defaultValue: 1 },
      { name: 'm2', type: 'number', description: 'Modulation index 2', defaultValue: 1 },
      { name: 'm3', type: 'number', description: 'Modulation index 3', defaultValue: 1 },
      { name: 'm4', type: 'number', description: 'Modulation index 4', defaultValue: 1 },
      { name: 'm5', type: 'number', description: 'Modulation index 5', defaultValue: 1 },
    ],
    returnType: 'number',
    description: 'DX7 algorithm 5: 6-operator FM synthesis with complex routing',
  },
  dx7_6: {
    name: 'dx7_6',
    parameters: [
      { name: 'hz', type: 'number', description: 'Base frequency in Hz' },
      { name: 'r1', type: 'number', description: 'Operator 1 frequency ratio', defaultValue: 0 },
      { name: 'r2', type: 'number', description: 'Operator 2 frequency ratio', defaultValue: 0 },
      { name: 'r3', type: 'number', description: 'Operator 3 frequency ratio', defaultValue: 0 },
      { name: 'r4', type: 'number', description: 'Operator 4 frequency ratio', defaultValue: 0 },
      { name: 'r5', type: 'number', description: 'Operator 5 frequency ratio', defaultValue: 0 },
      { name: 'r6', type: 'number', description: 'Operator 6 frequency ratio', defaultValue: 0 },
      { name: 'm1', type: 'number', description: 'Modulation index 1', defaultValue: 1 },
      { name: 'm2', type: 'number', description: 'Modulation index 2', defaultValue: 1 },
      { name: 'm3', type: 'number', description: 'Modulation index 3', defaultValue: 1 },
      { name: 'm4', type: 'number', description: 'Modulation index 4', defaultValue: 1 },
      { name: 'm5', type: 'number', description: 'Modulation index 5', defaultValue: 1 },
    ],
    returnType: 'number',
    description: 'DX7 algorithm 6: 6-operator FM synthesis with parallel carriers',
  },
  dx7_7: {
    name: 'dx7_7',
    parameters: [
      { name: 'hz', type: 'number', description: 'Base frequency in Hz' },
      { name: 'r1', type: 'number', description: 'Operator 1 frequency ratio', defaultValue: 0 },
      { name: 'r2', type: 'number', description: 'Operator 2 frequency ratio', defaultValue: 0 },
      { name: 'r3', type: 'number', description: 'Operator 3 frequency ratio', defaultValue: 0 },
      { name: 'r4', type: 'number', description: 'Operator 4 frequency ratio', defaultValue: 0 },
      { name: 'r5', type: 'number', description: 'Operator 5 frequency ratio', defaultValue: 0 },
      { name: 'r6', type: 'number', description: 'Operator 6 frequency ratio', defaultValue: 0 },
      { name: 'm1', type: 'number', description: 'Modulation index 1', defaultValue: 1 },
      { name: 'm2', type: 'number', description: 'Modulation index 2', defaultValue: 1 },
      { name: 'm3', type: 'number', description: 'Modulation index 3', defaultValue: 1 },
      { name: 'm4', type: 'number', description: 'Modulation index 4', defaultValue: 1 },
      { name: 'm5', type: 'number', description: 'Modulation index 5', defaultValue: 1 },
    ],
    returnType: 'number',
    description: 'DX7 algorithm 7: 6-operator FM synthesis with dual output',
  },
  dx7_8: {
    name: 'dx7_8',
    parameters: [
      { name: 'hz', type: 'number', description: 'Base frequency in Hz' },
      { name: 'r1', type: 'number', description: 'Operator 1 frequency ratio', defaultValue: 0 },
      { name: 'r2', type: 'number', description: 'Operator 2 frequency ratio', defaultValue: 0 },
      { name: 'r3', type: 'number', description: 'Operator 3 frequency ratio', defaultValue: 0 },
      { name: 'r4', type: 'number', description: 'Operator 4 frequency ratio', defaultValue: 0 },
      { name: 'r5', type: 'number', description: 'Operator 5 frequency ratio', defaultValue: 0 },
      { name: 'r6', type: 'number', description: 'Operator 6 frequency ratio', defaultValue: 0 },
      { name: 'm1', type: 'number', description: 'Modulation index 1', defaultValue: 1 },
      { name: 'm2', type: 'number', description: 'Modulation index 2', defaultValue: 1 },
      { name: 'm3', type: 'number', description: 'Modulation index 3', defaultValue: 1 },
      { name: 'm4', type: 'number', description: 'Modulation index 4', defaultValue: 1 },
      { name: 'm5', type: 'number', description: 'Modulation index 5', defaultValue: 1 },
    ],
    returnType: 'number',
    description: 'DX7 algorithm 8: 6-operator FM synthesis with multiple parallel outputs',
  },
  dx7_9: {
    name: 'dx7_9',
    parameters: [
      { name: 'hz', type: 'number', description: 'Base frequency in Hz' },
      { name: 'r1', type: 'number', description: 'Operator 1 frequency ratio', defaultValue: 0 },
      { name: 'r2', type: 'number', description: 'Operator 2 frequency ratio', defaultValue: 0 },
      { name: 'r3', type: 'number', description: 'Operator 3 frequency ratio', defaultValue: 0 },
      { name: 'r4', type: 'number', description: 'Operator 4 frequency ratio', defaultValue: 0 },
      { name: 'r5', type: 'number', description: 'Operator 5 frequency ratio', defaultValue: 0 },
      { name: 'r6', type: 'number', description: 'Operator 6 frequency ratio', defaultValue: 0 },
      { name: 'm1', type: 'number', description: 'Modulation index 1', defaultValue: 1 },
      { name: 'm2', type: 'number', description: 'Modulation index 2', defaultValue: 1 },
      { name: 'm3', type: 'number', description: 'Modulation index 3', defaultValue: 1 },
      { name: 'm4', type: 'number', description: 'Modulation index 4', defaultValue: 1 },
      { name: 'm5', type: 'number', description: 'Modulation index 5', defaultValue: 1 },
    ],
    returnType: 'number',
    description: 'DX7 algorithm 9: 6-operator FM synthesis with triple parallel output',
  },
  dx7_10: {
    name: 'dx7_10',
    parameters: [
      { name: 'hz', type: 'number', description: 'Base frequency in Hz' },
      { name: 'r1', type: 'number', description: 'Operator 1 frequency ratio', defaultValue: 0 },
      { name: 'r2', type: 'number', description: 'Operator 2 frequency ratio', defaultValue: 0 },
      { name: 'r3', type: 'number', description: 'Operator 3 frequency ratio', defaultValue: 0 },
      { name: 'r4', type: 'number', description: 'Operator 4 frequency ratio', defaultValue: 0 },
      { name: 'r5', type: 'number', description: 'Operator 5 frequency ratio', defaultValue: 0 },
      { name: 'r6', type: 'number', description: 'Operator 6 frequency ratio', defaultValue: 0 },
      { name: 'm1', type: 'number', description: 'Modulation index 1', defaultValue: 1 },
      { name: 'm2', type: 'number', description: 'Modulation index 2', defaultValue: 1 },
      { name: 'm3', type: 'number', description: 'Modulation index 3', defaultValue: 1 },
      { name: 'm4', type: 'number', description: 'Modulation index 4', defaultValue: 1 },
      { name: 'm5', type: 'number', description: 'Modulation index 5', defaultValue: 1 },
    ],
    returnType: 'number',
    description: 'DX7 algorithm 10: 6-operator FM synthesis with dual parallel output',
  },
  dx7_11: {
    name: 'dx7_11',
    parameters: [
      { name: 'hz', type: 'number', description: 'Base frequency in Hz' },
      { name: 'r1', type: 'number', description: 'Operator 1 frequency ratio', defaultValue: 0 },
      { name: 'r2', type: 'number', description: 'Operator 2 frequency ratio', defaultValue: 0 },
      { name: 'r3', type: 'number', description: 'Operator 3 frequency ratio', defaultValue: 0 },
      { name: 'r4', type: 'number', description: 'Operator 4 frequency ratio', defaultValue: 0 },
      { name: 'r5', type: 'number', description: 'Operator 5 frequency ratio', defaultValue: 0 },
      { name: 'r6', type: 'number', description: 'Operator 6 frequency ratio', defaultValue: 0 },
      { name: 'm1', type: 'number', description: 'Modulation index 1', defaultValue: 1 },
      { name: 'm2', type: 'number', description: 'Modulation index 2', defaultValue: 1 },
      { name: 'm3', type: 'number', description: 'Modulation index 3', defaultValue: 1 },
      { name: 'm4', type: 'number', description: 'Modulation index 4', defaultValue: 1 },
      { name: 'm5', type: 'number', description: 'Modulation index 5', defaultValue: 1 },
    ],
    returnType: 'number',
    description: 'DX7 algorithm 11: 6-operator FM synthesis with triple parallel output',
  },
  dx7_12: {
    name: 'dx7_12',
    parameters: [
      { name: 'hz', type: 'number', description: 'Base frequency in Hz' },
      { name: 'r1', type: 'number', description: 'Operator 1 frequency ratio', defaultValue: 0 },
      { name: 'r2', type: 'number', description: 'Operator 2 frequency ratio', defaultValue: 0 },
      { name: 'r3', type: 'number', description: 'Operator 3 frequency ratio', defaultValue: 0 },
      { name: 'r4', type: 'number', description: 'Operator 4 frequency ratio', defaultValue: 0 },
      { name: 'r5', type: 'number', description: 'Operator 5 frequency ratio', defaultValue: 0 },
      { name: 'r6', type: 'number', description: 'Operator 6 frequency ratio', defaultValue: 0 },
      { name: 'm1', type: 'number', description: 'Modulation index 1', defaultValue: 1 },
      { name: 'm2', type: 'number', description: 'Modulation index 2', defaultValue: 1 },
      { name: 'm3', type: 'number', description: 'Modulation index 3', defaultValue: 1 },
      { name: 'm4', type: 'number', description: 'Modulation index 4', defaultValue: 1 },
      { name: 'm5', type: 'number', description: 'Modulation index 5', defaultValue: 1 },
    ],
    returnType: 'number',
    description: 'DX7 algorithm 12: 6-operator FM synthesis with serial chain and parallel output',
  },
  dx7_13: {
    name: 'dx7_13',
    parameters: [
      { name: 'hz', type: 'number', description: 'Base frequency in Hz' },
      { name: 'r1', type: 'number', description: 'Operator 1 frequency ratio', defaultValue: 0 },
      { name: 'r2', type: 'number', description: 'Operator 2 frequency ratio', defaultValue: 0 },
      { name: 'r3', type: 'number', description: 'Operator 3 frequency ratio', defaultValue: 0 },
      { name: 'r4', type: 'number', description: 'Operator 4 frequency ratio', defaultValue: 0 },
      { name: 'r5', type: 'number', description: 'Operator 5 frequency ratio', defaultValue: 0 },
      { name: 'r6', type: 'number', description: 'Operator 6 frequency ratio', defaultValue: 0 },
      { name: 'm1', type: 'number', description: 'Modulation index 1', defaultValue: 1 },
      { name: 'm2', type: 'number', description: 'Modulation index 2', defaultValue: 1 },
      { name: 'm3', type: 'number', description: 'Modulation index 3', defaultValue: 1 },
      { name: 'm4', type: 'number', description: 'Modulation index 4', defaultValue: 1 },
      { name: 'm5', type: 'number', description: 'Modulation index 5', defaultValue: 1 },
    ],
    returnType: 'number',
    description: 'DX7 algorithm 13: 6-operator FM synthesis with quadruple parallel output',
  },
  dx7_14: {
    name: 'dx7_14',
    parameters: [
      { name: 'hz', type: 'number', description: 'Base frequency in Hz' },
      { name: 'r1', type: 'number', description: 'Operator 1 frequency ratio', defaultValue: 0 },
      { name: 'r2', type: 'number', description: 'Operator 2 frequency ratio', defaultValue: 0 },
      { name: 'r3', type: 'number', description: 'Operator 3 frequency ratio', defaultValue: 0 },
      { name: 'r4', type: 'number', description: 'Operator 4 frequency ratio', defaultValue: 0 },
      { name: 'r5', type: 'number', description: 'Operator 5 frequency ratio', defaultValue: 0 },
      { name: 'r6', type: 'number', description: 'Operator 6 frequency ratio', defaultValue: 0 },
      { name: 'm1', type: 'number', description: 'Modulation index 1', defaultValue: 1 },
      { name: 'm2', type: 'number', description: 'Modulation index 2', defaultValue: 1 },
      { name: 'm3', type: 'number', description: 'Modulation index 3', defaultValue: 1 },
      { name: 'm4', type: 'number', description: 'Modulation index 4', defaultValue: 1 },
      { name: 'm5', type: 'number', description: 'Modulation index 5', defaultValue: 1 },
    ],
    returnType: 'number',
    description: 'DX7 algorithm 14: 6-operator FM synthesis with full serial chain',
  },
  dx7_15: {
    name: 'dx7_15',
    parameters: [
      { name: 'hz', type: 'number', description: 'Base frequency in Hz' },
      { name: 'r1', type: 'number', description: 'Operator 1 frequency ratio', defaultValue: 0 },
      { name: 'r2', type: 'number', description: 'Operator 2 frequency ratio', defaultValue: 0 },
      { name: 'r3', type: 'number', description: 'Operator 3 frequency ratio', defaultValue: 0 },
      { name: 'r4', type: 'number', description: 'Operator 4 frequency ratio', defaultValue: 0 },
      { name: 'r5', type: 'number', description: 'Operator 5 frequency ratio', defaultValue: 0 },
      { name: 'r6', type: 'number', description: 'Operator 6 frequency ratio', defaultValue: 0 },
      { name: 'm1', type: 'number', description: 'Modulation index 1', defaultValue: 1 },
      { name: 'm2', type: 'number', description: 'Modulation index 2', defaultValue: 1 },
      { name: 'm3', type: 'number', description: 'Modulation index 3', defaultValue: 1 },
      { name: 'm4', type: 'number', description: 'Modulation index 4', defaultValue: 1 },
      { name: 'm5', type: 'number', description: 'Modulation index 5', defaultValue: 1 },
    ],
    returnType: 'number',
    description: 'DX7 algorithm 15: 6-operator FM synthesis with partial serial chain and parallel output',
  },
  dx7_16: {
    name: 'dx7_16',
    parameters: [
      { name: 'hz', type: 'number', description: 'Base frequency in Hz' },
      { name: 'r1', type: 'number', description: 'Operator 1 frequency ratio', defaultValue: 0 },
      { name: 'r2', type: 'number', description: 'Operator 2 frequency ratio', defaultValue: 0 },
      { name: 'r3', type: 'number', description: 'Operator 3 frequency ratio', defaultValue: 0 },
      { name: 'r4', type: 'number', description: 'Operator 4 frequency ratio', defaultValue: 0 },
      { name: 'r5', type: 'number', description: 'Operator 5 frequency ratio', defaultValue: 0 },
      { name: 'r6', type: 'number', description: 'Operator 6 frequency ratio', defaultValue: 0 },
      { name: 'm1', type: 'number', description: 'Modulation index 1', defaultValue: 1 },
      { name: 'm2', type: 'number', description: 'Modulation index 2', defaultValue: 1 },
      { name: 'm3', type: 'number', description: 'Modulation index 3', defaultValue: 1 },
      { name: 'm4', type: 'number', description: 'Modulation index 4', defaultValue: 1 },
      { name: 'm5', type: 'number', description: 'Modulation index 5', defaultValue: 1 },
    ],
    returnType: 'number',
    description: 'DX7 algorithm 16: 6-operator FM synthesis with minimal serial chain',
  },
  dx7_17: {
    name: 'dx7_17',
    parameters: [
      { name: 'hz', type: 'number', description: 'Base frequency in Hz' },
      { name: 'r1', type: 'number', description: 'Operator 1 frequency ratio', defaultValue: 0 },
      { name: 'r2', type: 'number', description: 'Operator 2 frequency ratio', defaultValue: 0 },
      { name: 'r3', type: 'number', description: 'Operator 3 frequency ratio', defaultValue: 0 },
      { name: 'r4', type: 'number', description: 'Operator 4 frequency ratio', defaultValue: 0 },
      { name: 'r5', type: 'number', description: 'Operator 5 frequency ratio', defaultValue: 0 },
      { name: 'r6', type: 'number', description: 'Operator 6 frequency ratio', defaultValue: 0 },
      { name: 'm1', type: 'number', description: 'Modulation index 1', defaultValue: 1 },
      { name: 'm2', type: 'number', description: 'Modulation index 2', defaultValue: 1 },
      { name: 'm3', type: 'number', description: 'Modulation index 3', defaultValue: 1 },
      { name: 'm4', type: 'number', description: 'Modulation index 4', defaultValue: 1 },
      { name: 'm5', type: 'number', description: 'Modulation index 5', defaultValue: 1 },
    ],
    returnType: 'number',
    description: 'DX7 algorithm 17: 6-operator FM synthesis with triple parallel output',
  },
  dx7_18: {
    name: 'dx7_18',
    parameters: [
      { name: 'hz', type: 'number', description: 'Base frequency in Hz' },
      { name: 'r1', type: 'number', description: 'Operator 1 frequency ratio', defaultValue: 0 },
      { name: 'r2', type: 'number', description: 'Operator 2 frequency ratio', defaultValue: 0 },
      { name: 'r3', type: 'number', description: 'Operator 3 frequency ratio', defaultValue: 0 },
      { name: 'r4', type: 'number', description: 'Operator 4 frequency ratio', defaultValue: 0 },
      { name: 'r5', type: 'number', description: 'Operator 5 frequency ratio', defaultValue: 0 },
      { name: 'r6', type: 'number', description: 'Operator 6 frequency ratio', defaultValue: 0 },
      { name: 'm1', type: 'number', description: 'Modulation index 1', defaultValue: 1 },
      { name: 'm2', type: 'number', description: 'Modulation index 2', defaultValue: 1 },
      { name: 'm3', type: 'number', description: 'Modulation index 3', defaultValue: 1 },
      { name: 'm4', type: 'number', description: 'Modulation index 4', defaultValue: 1 },
      { name: 'm5', type: 'number', description: 'Modulation index 5', defaultValue: 1 },
    ],
    returnType: 'number',
    description: 'DX7 algorithm 18: 6-operator FM synthesis with dual output and serial modulation',
  },
  dx7_19: {
    name: 'dx7_19',
    parameters: [
      { name: 'hz', type: 'number', description: 'Base frequency in Hz' },
      { name: 'r1', type: 'number', description: 'Operator 1 frequency ratio', defaultValue: 0 },
      { name: 'r2', type: 'number', description: 'Operator 2 frequency ratio', defaultValue: 0 },
      { name: 'r3', type: 'number', description: 'Operator 3 frequency ratio', defaultValue: 0 },
      { name: 'r4', type: 'number', description: 'Operator 4 frequency ratio', defaultValue: 0 },
      { name: 'r5', type: 'number', description: 'Operator 5 frequency ratio', defaultValue: 0 },
      { name: 'r6', type: 'number', description: 'Operator 6 frequency ratio', defaultValue: 0 },
      { name: 'm1', type: 'number', description: 'Modulation index 1', defaultValue: 1 },
      { name: 'm2', type: 'number', description: 'Modulation index 2', defaultValue: 1 },
      { name: 'm3', type: 'number', description: 'Modulation index 3', defaultValue: 1 },
      { name: 'm4', type: 'number', description: 'Modulation index 4', defaultValue: 1 },
      { name: 'm5', type: 'number', description: 'Modulation index 5', defaultValue: 1 },
    ],
    returnType: 'number',
    description: 'DX7 algorithm 19: 6-operator FM synthesis with triple parallel output and feedback',
  },
  dx7_20: {
    name: 'dx7_20',
    parameters: [
      { name: 'hz', type: 'number', description: 'Base frequency in Hz' },
      { name: 'r1', type: 'number', description: 'Operator 1 frequency ratio', defaultValue: 0 },
      { name: 'r2', type: 'number', description: 'Operator 2 frequency ratio', defaultValue: 0 },
      { name: 'r3', type: 'number', description: 'Operator 3 frequency ratio', defaultValue: 0 },
      { name: 'r4', type: 'number', description: 'Operator 4 frequency ratio', defaultValue: 0 },
      { name: 'r5', type: 'number', description: 'Operator 5 frequency ratio', defaultValue: 0 },
      { name: 'r6', type: 'number', description: 'Operator 6 frequency ratio', defaultValue: 0 },
      { name: 'm1', type: 'number', description: 'Modulation index 1', defaultValue: 1 },
      { name: 'm2', type: 'number', description: 'Modulation index 2', defaultValue: 1 },
      { name: 'm3', type: 'number', description: 'Modulation index 3', defaultValue: 1 },
      { name: 'm4', type: 'number', description: 'Modulation index 4', defaultValue: 1 },
      { name: 'm5', type: 'number', description: 'Modulation index 5', defaultValue: 1 },
    ],
    returnType: 'number',
    description: 'DX7 algorithm 20: 6-operator FM synthesis with triple parallel output',
  },
  dx7_21: {
    name: 'dx7_21',
    parameters: [
      { name: 'hz', type: 'number', description: 'Base frequency in Hz' },
      { name: 'r1', type: 'number', description: 'Operator 1 frequency ratio', defaultValue: 0 },
      { name: 'r2', type: 'number', description: 'Operator 2 frequency ratio', defaultValue: 0 },
      { name: 'r3', type: 'number', description: 'Operator 3 frequency ratio', defaultValue: 0 },
      { name: 'r4', type: 'number', description: 'Operator 4 frequency ratio', defaultValue: 0 },
      { name: 'r5', type: 'number', description: 'Operator 5 frequency ratio', defaultValue: 0 },
      { name: 'r6', type: 'number', description: 'Operator 6 frequency ratio', defaultValue: 0 },
      { name: 'm1', type: 'number', description: 'Modulation index 1', defaultValue: 1 },
      { name: 'm2', type: 'number', description: 'Modulation index 2', defaultValue: 1 },
      { name: 'm3', type: 'number', description: 'Modulation index 3', defaultValue: 1 },
      { name: 'm4', type: 'number', description: 'Modulation index 4', defaultValue: 1 },
      { name: 'm5', type: 'number', description: 'Modulation index 5', defaultValue: 1 },
    ],
    returnType: 'number',
    description: 'DX7 algorithm 21: 6-operator FM synthesis with quadruple parallel output',
  },
  dx7_22: {
    name: 'dx7_22',
    parameters: [
      { name: 'hz', type: 'number', description: 'Base frequency in Hz' },
      { name: 'r1', type: 'number', description: 'Operator 1 frequency ratio', defaultValue: 0 },
      { name: 'r2', type: 'number', description: 'Operator 2 frequency ratio', defaultValue: 0 },
      { name: 'r3', type: 'number', description: 'Operator 3 frequency ratio', defaultValue: 0 },
      { name: 'r4', type: 'number', description: 'Operator 4 frequency ratio', defaultValue: 0 },
      { name: 'r5', type: 'number', description: 'Operator 5 frequency ratio', defaultValue: 0 },
      { name: 'r6', type: 'number', description: 'Operator 6 frequency ratio', defaultValue: 0 },
      { name: 'm1', type: 'number', description: 'Modulation index 1', defaultValue: 1 },
      { name: 'm2', type: 'number', description: 'Modulation index 2', defaultValue: 1 },
      { name: 'm3', type: 'number', description: 'Modulation index 3', defaultValue: 1 },
      { name: 'm4', type: 'number', description: 'Modulation index 4', defaultValue: 1 },
      { name: 'm5', type: 'number', description: 'Modulation index 5', defaultValue: 1 },
    ],
    returnType: 'number',
    description: 'DX7 algorithm 22: 6-operator FM synthesis with dual output and serial modulation',
  },
  dx7_23: {
    name: 'dx7_23',
    parameters: [
      { name: 'hz', type: 'number', description: 'Base frequency in Hz' },
      { name: 'r1', type: 'number', description: 'Operator 1 frequency ratio', defaultValue: 0 },
      { name: 'r2', type: 'number', description: 'Operator 2 frequency ratio', defaultValue: 0 },
      { name: 'r3', type: 'number', description: 'Operator 3 frequency ratio', defaultValue: 0 },
      { name: 'r4', type: 'number', description: 'Operator 4 frequency ratio', defaultValue: 0 },
      { name: 'r5', type: 'number', description: 'Operator 5 frequency ratio', defaultValue: 0 },
      { name: 'r6', type: 'number', description: 'Operator 6 frequency ratio', defaultValue: 0 },
      { name: 'm1', type: 'number', description: 'Modulation index 1', defaultValue: 1 },
      { name: 'm2', type: 'number', description: 'Modulation index 2', defaultValue: 1 },
      { name: 'm3', type: 'number', description: 'Modulation index 3', defaultValue: 1 },
      { name: 'm4', type: 'number', description: 'Modulation index 4', defaultValue: 1 },
      { name: 'm5', type: 'number', description: 'Modulation index 5', defaultValue: 1 },
    ],
    returnType: 'number',
    description: 'DX7 algorithm 23: 6-operator FM synthesis with triple parallel output and feedback',
  },
  dx7_24: {
    name: 'dx7_24',
    parameters: [
      { name: 'hz', type: 'number', description: 'Base frequency in Hz' },
      { name: 'r1', type: 'number', description: 'Operator 1 frequency ratio', defaultValue: 0 },
      { name: 'r2', type: 'number', description: 'Operator 2 frequency ratio', defaultValue: 0 },
      { name: 'r3', type: 'number', description: 'Operator 3 frequency ratio', defaultValue: 0 },
      { name: 'r4', type: 'number', description: 'Operator 4 frequency ratio', defaultValue: 0 },
      { name: 'r5', type: 'number', description: 'Operator 5 frequency ratio', defaultValue: 0 },
      { name: 'r6', type: 'number', description: 'Operator 6 frequency ratio', defaultValue: 0 },
      { name: 'm1', type: 'number', description: 'Modulation index 1', defaultValue: 1 },
      { name: 'm2', type: 'number', description: 'Modulation index 2', defaultValue: 1 },
      { name: 'm3', type: 'number', description: 'Modulation index 3', defaultValue: 1 },
      { name: 'm4', type: 'number', description: 'Modulation index 4', defaultValue: 1 },
      { name: 'm5', type: 'number', description: 'Modulation index 5', defaultValue: 1 },
    ],
    returnType: 'number',
    description: 'DX7 algorithm 24: 6-operator FM synthesis with triple parallel output and serial modulation',
  },
  dx7_25: {
    name: 'dx7_25',
    parameters: [
      { name: 'hz', type: 'number', description: 'Base frequency in Hz' },
      { name: 'r1', type: 'number', description: 'Operator 1 frequency ratio', defaultValue: 0 },
      { name: 'r2', type: 'number', description: 'Operator 2 frequency ratio', defaultValue: 0 },
      { name: 'r3', type: 'number', description: 'Operator 3 frequency ratio', defaultValue: 0 },
      { name: 'r4', type: 'number', description: 'Operator 4 frequency ratio', defaultValue: 0 },
      { name: 'r5', type: 'number', description: 'Operator 5 frequency ratio', defaultValue: 0 },
      { name: 'r6', type: 'number', description: 'Operator 6 frequency ratio', defaultValue: 0 },
      { name: 'm1', type: 'number', description: 'Modulation index 1', defaultValue: 1 },
      { name: 'm2', type: 'number', description: 'Modulation index 2', defaultValue: 1 },
      { name: 'm3', type: 'number', description: 'Modulation index 3', defaultValue: 1 },
      { name: 'm4', type: 'number', description: 'Modulation index 4', defaultValue: 1 },
      { name: 'm5', type: 'number', description: 'Modulation index 5', defaultValue: 1 },
    ],
    returnType: 'number',
    description: 'DX7 algorithm 25: 6-operator FM synthesis with triple parallel output and full serial chain',
  },
  dx7_26: {
    name: 'dx7_26',
    parameters: [
      { name: 'hz', type: 'number', description: 'Base frequency in Hz' },
      { name: 'r1', type: 'number', description: 'Operator 1 frequency ratio', defaultValue: 0 },
      { name: 'r2', type: 'number', description: 'Operator 2 frequency ratio', defaultValue: 0 },
      { name: 'r3', type: 'number', description: 'Operator 3 frequency ratio', defaultValue: 0 },
      { name: 'r4', type: 'number', description: 'Operator 4 frequency ratio', defaultValue: 0 },
      { name: 'r5', type: 'number', description: 'Operator 5 frequency ratio', defaultValue: 0 },
      { name: 'r6', type: 'number', description: 'Operator 6 frequency ratio', defaultValue: 0 },
      { name: 'm1', type: 'number', description: 'Modulation index 1', defaultValue: 1 },
      { name: 'm2', type: 'number', description: 'Modulation index 2', defaultValue: 1 },
      { name: 'm3', type: 'number', description: 'Modulation index 3', defaultValue: 1 },
      { name: 'm4', type: 'number', description: 'Modulation index 4', defaultValue: 1 },
      { name: 'm5', type: 'number', description: 'Modulation index 5', defaultValue: 1 },
    ],
    returnType: 'number',
    description: 'DX7 algorithm 26: 6-operator FM synthesis with full serial chain output',
  },
  dx7_27: {
    name: 'dx7_27',
    parameters: [
      { name: 'hz', type: 'number', description: 'Base frequency in Hz' },
      { name: 'r1', type: 'number', description: 'Operator 1 frequency ratio', defaultValue: 0 },
      { name: 'r2', type: 'number', description: 'Operator 2 frequency ratio', defaultValue: 0 },
      { name: 'r3', type: 'number', description: 'Operator 3 frequency ratio', defaultValue: 0 },
      { name: 'r4', type: 'number', description: 'Operator 4 frequency ratio', defaultValue: 0 },
      { name: 'r5', type: 'number', description: 'Operator 5 frequency ratio', defaultValue: 0 },
      { name: 'r6', type: 'number', description: 'Operator 6 frequency ratio', defaultValue: 0 },
      { name: 'm1', type: 'number', description: 'Modulation index 1', defaultValue: 1 },
      { name: 'm2', type: 'number', description: 'Modulation index 2', defaultValue: 1 },
      { name: 'm3', type: 'number', description: 'Modulation index 3', defaultValue: 1 },
      { name: 'm4', type: 'number', description: 'Modulation index 4', defaultValue: 1 },
      { name: 'm5', type: 'number', description: 'Modulation index 5', defaultValue: 1 },
    ],
    returnType: 'number',
    description: 'DX7 algorithm 27: 6-operator FM synthesis with triple parallel output and partial serial chain',
  },
  dx7_28: {
    name: 'dx7_28',
    parameters: [
      { name: 'hz', type: 'number', description: 'Base frequency in Hz' },
      { name: 'r1', type: 'number', description: 'Operator 1 frequency ratio', defaultValue: 0 },
      { name: 'r2', type: 'number', description: 'Operator 2 frequency ratio', defaultValue: 0 },
      { name: 'r3', type: 'number', description: 'Operator 3 frequency ratio', defaultValue: 0 },
      { name: 'r4', type: 'number', description: 'Operator 4 frequency ratio', defaultValue: 0 },
      { name: 'r5', type: 'number', description: 'Operator 5 frequency ratio', defaultValue: 0 },
      { name: 'r6', type: 'number', description: 'Operator 6 frequency ratio', defaultValue: 0 },
      { name: 'm1', type: 'number', description: 'Modulation index 1', defaultValue: 1 },
      { name: 'm2', type: 'number', description: 'Modulation index 2', defaultValue: 1 },
      { name: 'm3', type: 'number', description: 'Modulation index 3', defaultValue: 1 },
      { name: 'm4', type: 'number', description: 'Modulation index 4', defaultValue: 1 },
      { name: 'm5', type: 'number', description: 'Modulation index 5', defaultValue: 1 },
    ],
    returnType: 'number',
    description: 'DX7 algorithm 28: 6-operator FM synthesis with triple parallel output and dual serial chains',
  },
  dx7_29: {
    name: 'dx7_29',
    parameters: [
      { name: 'hz', type: 'number', description: 'Base frequency in Hz' },
      { name: 'r1', type: 'number', description: 'Operator 1 frequency ratio', defaultValue: 0 },
      { name: 'r2', type: 'number', description: 'Operator 2 frequency ratio', defaultValue: 0 },
      { name: 'r3', type: 'number', description: 'Operator 3 frequency ratio', defaultValue: 0 },
      { name: 'r4', type: 'number', description: 'Operator 4 frequency ratio', defaultValue: 0 },
      { name: 'r5', type: 'number', description: 'Operator 5 frequency ratio', defaultValue: 0 },
      { name: 'r6', type: 'number', description: 'Operator 6 frequency ratio', defaultValue: 0 },
      { name: 'm1', type: 'number', description: 'Modulation index 1', defaultValue: 1 },
      { name: 'm2', type: 'number', description: 'Modulation index 2', defaultValue: 1 },
      { name: 'm3', type: 'number', description: 'Modulation index 3', defaultValue: 1 },
      { name: 'm4', type: 'number', description: 'Modulation index 4', defaultValue: 1 },
      { name: 'm5', type: 'number', description: 'Modulation index 5', defaultValue: 1 },
    ],
    returnType: 'number',
    description: 'DX7 algorithm 29: 6-operator FM synthesis with full serial chain and parallel output',
  },
  dx7_30: {
    name: 'dx7_30',
    parameters: [
      { name: 'hz', type: 'number', description: 'Base frequency in Hz' },
      { name: 'r1', type: 'number', description: 'Operator 1 frequency ratio', defaultValue: 0 },
      { name: 'r2', type: 'number', description: 'Operator 2 frequency ratio', defaultValue: 0 },
      { name: 'r3', type: 'number', description: 'Operator 3 frequency ratio', defaultValue: 0 },
      { name: 'r4', type: 'number', description: 'Operator 4 frequency ratio', defaultValue: 0 },
      { name: 'r5', type: 'number', description: 'Operator 5 frequency ratio', defaultValue: 0 },
      { name: 'r6', type: 'number', description: 'Operator 6 frequency ratio', defaultValue: 0 },
      { name: 'm1', type: 'number', description: 'Modulation index 1', defaultValue: 1 },
      { name: 'm2', type: 'number', description: 'Modulation index 2', defaultValue: 1 },
      { name: 'm3', type: 'number', description: 'Modulation index 3', defaultValue: 1 },
      { name: 'm4', type: 'number', description: 'Modulation index 4', defaultValue: 1 },
      { name: 'm5', type: 'number', description: 'Modulation index 5', defaultValue: 1 },
    ],
    returnType: 'number',
    description: 'DX7 algorithm 30: 6-operator FM synthesis with quadruple parallel output and serial chain',
  },
  dx7_31: {
    name: 'dx7_31',
    parameters: [
      { name: 'hz', type: 'number', description: 'Base frequency in Hz' },
      { name: 'r1', type: 'number', description: 'Operator 1 frequency ratio', defaultValue: 0 },
      { name: 'r2', type: 'number', description: 'Operator 2 frequency ratio', defaultValue: 0 },
      { name: 'r3', type: 'number', description: 'Operator 3 frequency ratio', defaultValue: 0 },
      { name: 'r4', type: 'number', description: 'Operator 4 frequency ratio', defaultValue: 0 },
      { name: 'r5', type: 'number', description: 'Operator 5 frequency ratio', defaultValue: 0 },
      { name: 'r6', type: 'number', description: 'Operator 6 frequency ratio', defaultValue: 0 },
      { name: 'm1', type: 'number', description: 'Modulation index 1', defaultValue: 1 },
      { name: 'm2', type: 'number', description: 'Modulation index 2', defaultValue: 1 },
      { name: 'm3', type: 'number', description: 'Modulation index 3', defaultValue: 1 },
      { name: 'm4', type: 'number', description: 'Modulation index 4', defaultValue: 1 },
      { name: 'm5', type: 'number', description: 'Modulation index 5', defaultValue: 1 },
    ],
    returnType: 'number',
    description: 'DX7 algorithm 31: 6-operator FM synthesis with all operators in parallel',
  },
  dx7_32: {
    name: 'dx7_32',
    parameters: [
      { name: 'hz', type: 'number', description: 'Base frequency in Hz' },
      { name: 'r1', type: 'number', description: 'Operator 1 frequency ratio', defaultValue: 0 },
      { name: 'r2', type: 'number', description: 'Operator 2 frequency ratio', defaultValue: 0 },
      { name: 'r3', type: 'number', description: 'Operator 3 frequency ratio', defaultValue: 0 },
      { name: 'r4', type: 'number', description: 'Operator 4 frequency ratio', defaultValue: 0 },
      { name: 'r5', type: 'number', description: 'Operator 5 frequency ratio', defaultValue: 0 },
      { name: 'r6', type: 'number', description: 'Operator 6 frequency ratio', defaultValue: 0 },
      { name: 'm1', type: 'number', description: 'Modulation index 1', defaultValue: 1 },
      { name: 'm2', type: 'number', description: 'Modulation index 2', defaultValue: 1 },
      { name: 'm3', type: 'number', description: 'Modulation index 3', defaultValue: 1 },
      { name: 'm4', type: 'number', description: 'Modulation index 4', defaultValue: 1 },
      { name: 'm5', type: 'number', description: 'Modulation index 5', defaultValue: 1 },
    ],
    returnType: 'number',
    description: 'DX7 algorithm 32: 6-operator FM synthesis with all operators in parallel (identical to algorithm 31)',
  },
  timeline: {
    name: 'timeline',
    parameters: [
      { name: 'beat', type: 'number', description: 'The beat division (e.g. 4/4, 1/4)' },
      { name: 'pattern', type: 'string', description: 'Timeline pattern string (e.g. "/4 \'4 .4 \\4")' },
    ],
    returnType: 'number',
    description:
      'Returns a value 0..1 based on timeline pattern. `/4` rises for 4 divisions, `\'` holds at 1, `.` holds at 0, `\\4` falls for 4 divisions. Numbers after `\'` or `.` specify divisions (e.g. `\'4` instead of `\'\'\'\'`).',
    examples: [
      'timeline(beat:4/4, pattern:"/4 \'4 .4 \\4")',
      'timeline(beat:1/4, pattern:"/2 \'\'\' . . \\2")',
    ],
  },
}

const allFunctionNames = Object.keys(functionDefinitions)

export function getAllFunctionNames(): string[] {
  return allFunctionNames
}

const allArgumentNames = Object.values(functionDefinitions).flatMap(definition =>
  definition.parameters.map(parameter => parameter.name)
)

export function getAllArgumentNames(): string[] {
  return allArgumentNames
}
